// Imports from core modules
import { S, save, stampItem, tombstoneId } from '../core/state.js';
import { effectiveStop, netWorthUSD, riskFxExposure, tradeExposure } from '../core/risk-calc.js';
import { mergeThesisOnEdit, buildThesis, validWatch, watchSentence, invalidationFired } from '../core/thesis.js';
import { tradeSignalStance } from './signals.js';
import { stanceCard, stanceOrder } from '../ui/stance-card.js';
import { esc, f, fU, fT, fP, fByCur, pc, bc, uid, now, normTicker, toUSD, toUSDStrict, realizedUSD, isInterestRealized, realizedOrigin, tradeForRealized, realizedForTrade } from '../core/utils.js';
import { priceSale } from '../core/fees.js';
import { tdAccrued, hVal, hTotUSD } from '../core/math.js';
import { toast, clMo } from '../ui/layout.js';
import { markDirty } from '../app/render-loop.js';

// Mutable editor state
let editTrId = null;

// ===== Notifications & Scanning =====

function notify(title, body, key) {
  // Delegate to SW-based push notification system (window.swNotify wired via main.js)
  if (typeof window.swNotify === 'function') {
    window.swNotify('tradeAlert', title, body, { dedupeKey: key, tag: key || title, action: 'trades' });
    return;
  }
  // Fallback: original direct Notification API (if push module not loaded)
  if (key && S.alertedKeys[key]) return;
  if (key) S.alertedKeys[key] = Date.now();
  if (S.settings && S.settings.notif && 'Notification' in window && Notification.permission === 'granted') {
    try {
      new Notification('SPEKTRX — ' + title, { body: body, tag: key || title });
    } catch (e) {
      console.warn('Notify failed', e);
    }
  }
  toast(title + ': ' + body, 'a-y');
  S.history && S.history.unshift && S.history.unshift({ id: uid(), date: now(), type: 'alert', ticker: '', msg: title + ' — ' + body });
  save && save();
}

// Scan trades for SL/TP1/TP2 cross. prevPrices = snapshot before refresh
function scanTradeCrossings(prevPrices) {
  let _thxFired = false;
  S.trades.filter(t => t.status !== 'Closed' && t.type !== 'TimeDeposit').forEach(t => {
    const cur = S.prices[t.ticker];
    if (!cur) return;
    const prev = prevPrices[t.ticker] ? prevPrices[t.ticker].price : null;
    const px = cur.price;
    const check = (level, label) => {
      if (!level) return;
      const key = `tr_${t.id}_${label}`;
      let triggered = false;
      if (t.side === 'Long') {
        if (label === 'SL' && px <= level && (prev == null || prev > level)) triggered = true;
        if ((label === 'TP1' || label === 'TP2') && px >= level && (prev == null || prev < level)) triggered = true;
      } else {
        if (label === 'SL' && px >= level && (prev == null || prev < level)) triggered = true;
        if ((label === 'TP1' || label === 'TP2') && px <= level && (prev == null || prev > level)) triggered = true;
      }
      if (triggered) {
        // Use swNotify if available (passes ticker for notification center)
        if (typeof window.swNotify === 'function') {
          window.swNotify('tradeAlert', `${t.ticker} ${label} hit`,
            `Price ${fByCur(t.currency || 'USD', px)} crossed ${label} ${fByCur(t.currency || 'USD', level)}. Manual confirmation required.`,
            { dedupeKey: key, tag: key, ticker: t.ticker, action: 'trades', requireInteraction: label === 'SL' }
          );
        } else {
          notify(`${t.ticker} ${label} hit`,
            `Price ${fByCur(t.currency || 'USD', px)} crossed ${label} ${fByCur(t.currency || 'USD', level)}. Manual confirmation required.`,
            key);
        }
      }
    };
    check(t.sl, 'SL');
    check(t.tp1, 'TP1');
    check(t.tp2, 'TP2');

    // ═══ PART 1b — THE THESIS INVALIDATION ═══
    //
    // Different from SL/TP above in two ways that matter.
    //
    // 1. SL/TP fire on a CROSSING (`prev > level && px <= level`) so they alert once per crossing
    //    and stay silent while price sits beyond. An invalidation is not an alert about price, it
    //    is the moment a REASON stopped being true. It fires ONCE, ever, and is written down —
    //    `firedAt` — so the journal can still say so months later when the trade is closed and
    //    the price has recovered. That is the whole reason it is stamped rather than derived.
    // 2. Because it is stamped, this is a STATE WRITE inside a scanner that never wrote before.
    //    `stampItem` bumps `_updatedAt` or the cloud merge treats it as the stale side, and
    //    `save()` runs once at the end of the sweep, only when something actually changed —
    //    price refresh is frequent and an unconditional save on every tick would be a new writer
    //    firing constantly for nothing.
    if (t.thesis && !t.thesis.firedAt && invalidationFired(t.thesis, px)) {
      t.thesis.firedAt = now();
      stampItem(t);
      _thxFired = true;
      const key = `thx_${t.id}`;
      const body = `${watchSentence(t.thesis)} — the reason you opened it no longer holds. `
        + (t.thesis.invalidation ? `"${t.thesis.invalidation}"` : 'Review the position.');
      if (typeof window.swNotify === 'function') {
        window.swNotify('tradeAlert', `${t.ticker} thesis invalidated`, body,
          { dedupeKey: key, tag: key, ticker: t.ticker, action: 'trades', requireInteraction: true });
      } else {
        notify(`${t.ticker} thesis invalidated`, body, key);
      }
    }
  });
  // One save for the whole sweep, and only if a stamp landed.
  if (_thxFired) save();
}

function scanMaturities() {
  S.trades
    .filter(t => t.type === 'TimeDeposit' && t.td && !t.td.confirmed && t.status !== 'Closed')
    .forEach(t => {
      const a = tdAccrued(t.td);
      if (!a) return;
      const _n = (title, body, key) => {
        if (typeof window.swNotify === 'function') {
          window.swNotify('tdMaturity', title, body, { dedupeKey: key, tag: key, ticker: t.ticker, action: 'trades' });
        } else {
          notify(title, body, key);
        }
      };
      if (a.matured)
        _n(`${t.ticker} matured`, `Net ${f(a.netMaturity)} ${t.currency}. Confirm in Trades.`, `tdmat_${t.id}`);
      else if (a.daysToMat === 7)
        _n(`${t.ticker} matures in 7d`, `On ${a.maturityDate.slice(0, 10)}.`, `td7_${t.id}`);
      else if (a.daysToMat === 1)
        _n(`${t.ticker} matures tomorrow`, `On ${a.maturityDate.slice(0, 10)}.`, `td1_${t.id}`);
    });
}

// ===== Inline Price Editor =====

function inlineTPrice(tk, cur) {
  // Find the clickable span via data-action attribute
  const el = document.querySelector(`[data-action="inlineTPrice"][data-args*='"${tk}"']`);
  if (!el) return;
  const inp = document.createElement('input');
  inp.className = 'price-input';
  inp.type = 'number';
  inp.step = 'any';
  inp.value = cur;
  inp.style.width = '90px';
  el.replaceWith(inp);
  inp.focus();
  inp.select();
  const commit = () => {
    const v = parseFloat(inp.value);
    if (v && v !== cur) {
      manTP(tk, v);
    } else {
      renderT();
    }
  };
  inp.addEventListener('keydown', e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
    if (e.key === 'Escape') {
      renderT();
    }
  });
  inp.addEventListener('blur', commit);
}

function manTP(tkOrEvent, val) {
  let tk, p;
  if (tkOrEvent && tkOrEvent.target) {
    // Called via data-action-change delegation: manTP(event)
    const e = tkOrEvent;
    const args = e.target.getAttribute('data-args');
    tk = args ? JSON.parse(args)[0] : null;
    p = parseFloat(e.target.value);
  } else {
    tk = tkOrEvent;
    p = parseFloat(val);
  }
  if (!tk || !p) return;
  S.prices[tk] = { price: p, changePct: 0, src: 'manual', ts: Date.now(), cur: 'USD' };
  console.log('Trade manual:', tk, p);
  save();
  renderT();
}

// ===== TimeDeposit Form Helpers =====

function toggleTdTradeFields() {
  const t = document.getElementById('tTp').value;
  const isTd = t === 'TimeDeposit';
  document.getElementById('tdTradeFields').style.display = isTd ? 'block' : 'none';
  // Hide irrelevant fields for TD
  ['tSd', 'tLv', 'tSl', 'tT1', 'tT1p', 'tT2', 'tT2p', 'tRate', 'tDur'].forEach(id => {
    const el = document.getElementById(id);
    if (!el) return;
    const fgl = el.closest('.fgl');
    if (fgl) fgl.style.display = isTd ? 'none' : '';
  });
  if (isTd) calcTdTradePreview();
}

function onTdKindChange() {
  const k = document.getElementById('tdKind').value;
  const isDaily = k === 'daily';
  document.getElementById('tdTermWrap').style.display = isDaily ? 'none' : '';
  document.getElementById('tdModeWrap').style.display = isDaily ? 'none' : '';
  document.getElementById('tdMatValWrap').style.display = isDaily ? 'none' : '';
  // TR default withholding: daily 17.5%, monthly 10%
  const wht = document.getElementById('tdWht');
  if (wht && !wht.dataset.touched) wht.value = isDaily ? '17.5' : '10';
  calcTdTradePreview();
}

function calcTdTradePreview() {
  const td = {
    kind: document.getElementById('tdKind').value,
    principal: parseFloat(document.getElementById('tdPrincipal').value) || 0,
    startDate: document.getElementById('tdStart').value || null,
    termMonths: parseInt(document.getElementById('tdTerm').value) || 0,
    mode: document.getElementById('tdMode').value,
    annualRate: parseFloat(document.getElementById('tdRate2').value) || null,
    maturityValue: parseFloat(document.getElementById('tdMatVal').value) || null,
    withholdingPct: parseFloat(document.getElementById('tdWht').value) || 0,
    nonEarning: parseFloat(document.getElementById('tdNonEarn').value) || 0
  };
  const a = tdAccrued(td);
  const el = document.getElementById('tdPreview');
  if (!a) {
    el.innerHTML =
      '<span style="color:var(--t3)">Fill principal, start date, and rate (term required for monthly)…</span>';
    return;
  }
  const _ne = Number(td.nonEarning) || 0;
  const inflation = (S.settings?.trInflation || 0) * 100;
  const realReturn = (((1 + a.rate / 100) / (1 + inflation / 100) - 1) * 100);
  if (td.kind === 'daily') {
    const dgStr = a.dailyGross ? f(a.dailyGross) : '—';
    const dnStr = a.dailyNet ? f(a.dailyNet) : '—';
    el.innerHTML = `<strong>Daily</strong> · Annual Rate: <strong>${a.rate.toFixed(
      2
    )}%</strong> | Days Open: <strong>${a.daysOpen}</strong><br>Daily Gross: <strong>${dgStr}</strong> | Daily Net: <strong>${dnStr}</strong> | Withholding ${td.withholdingPct}%<br>Accrued Net: <strong>${f(
      a.accruedNet
    )}</strong> | Current Value: <strong>${f(a.currentValue)}</strong><br>Real Return vs ${inflation.toFixed(
      0
    )}% inflation: <strong style="color:${realReturn >= 0 ? 'var(--grn)' : 'var(--red)'}">${realReturn.toFixed(
      2
    )}%</strong>${_ne > 0 ? `<br><span style="color:var(--yel)">Earning base: <strong>${f(td.principal - _ne)}</strong> of ${f(td.principal)} — ${f(_ne)} earns nothing</span>` : ''}`;
  } else {
    el.innerHTML = `Effective Rate: <strong>${a.rate.toFixed(2)}%</strong> | Gross Maturity: <strong>${f(
      a.gross
    )}</strong> | Net Maturity: <strong>${f(a.netMaturity)}</strong> | Net Interest: <strong>${f(
      a.netInt
    )}</strong><br>Maturity: <strong>${a.maturityDate.slice(0, 10)}</strong> (${a.daysToMat}d) | Real Return vs ${inflation.toFixed(
      0
    )}% inflation: <strong style="color:${realReturn >= 0 ? 'var(--grn)' : 'var(--red)'}">${realReturn.toFixed(
      2
    )}%</strong>`;
  }
}

// ===== Trade P&L Calculator =====

function calcExpPnl() {
  const tp = document.getElementById('tTp').value;
  const en = parseFloat(document.getElementById('tEn').value) || 0;
  const qt = parseFloat(document.getElementById('tQt').value) || 0;
  const cur = document.getElementById('tCur').value;
  const fc = v => fByCur(cur, v);
  const el = document.getElementById('tExpPnl');
  if (tp === 'Deposit') {
    const rate = parseFloat(document.getElementById('tRate').value) || 0;
    const dur = parseFloat(document.getElementById('tDur').value) || 0;
    const pnl = en * qt * (rate / 100) * (dur / 12);
    el.value = pnl ? fc(pnl) + ' (' + dur + 'm @ ' + rate + '%)' : '';
  } else {
    const tp1 = parseFloat(document.getElementById('tT1').value) || 0;
    const lev = parseFloat(document.getElementById('tLv').value) || 1;
    const side = document.getElementById('tSd').value;
    if (tp1 && en && qt) {
      const pnl = (side === 'Long' ? tp1 - en : en - tp1) * qt * lev;
      el.value = fc(pnl) + ' (to TP1)';
    } else el.value = '';
  }
}

// ===== Main Trades Table Renderer =====

function renderT() {
  const act = S.trades.filter(t => t.status !== 'Closed');
  // group P&L by currency
  const pnlByCur = {};
  act.forEach(t => {
    const p = S.prices[t.ticker];
    if (!p) return;
    const cur = t.currency || 'USD';
    const pl = (t.side === 'Long' ? p.price - t.entry : t.entry - p.price) * t.qty * t.leverage;
    if (!pnlByCur[cur]) pnlByCur[cur] = 0;
    pnlByCur[cur] += pl;
  });
  const rByCur = {};
  S.realized.forEach(r => {
    const t = S.trades.find(x => x.id === r.id) || S.trades.find(x => x.ticker === r.ticker);
    const cur = t?.currency || 'USD';
    if (!rByCur[cur]) rByCur[cur] = 0;
    rByCur[cur] += r.pnl;
  });
  const pnlHtml =
    Object.entries(pnlByCur)
      .map(([c, v]) => `<span class="${pc(v)}">${fByCur(c, v)}</span>`)
      .join(' ') || '—';
  const rHtml =
    Object.entries(rByCur)
      .map(([c, v]) => `<span class="${pc(v)}">${fByCur(c, v)}</span>`)
      .join(' ') || fU(0);
  const totByCur = {};
  Object.entries(pnlByCur).forEach(([c, v]) => {
    totByCur[c] = (totByCur[c] || 0) + v;
  });
  Object.entries(rByCur).forEach(([c, v]) => {
    totByCur[c] = (totByCur[c] || 0) + v;
  });
  const totHtml =
    Object.entries(totByCur)
      .map(([c, v]) => `<span class="${pc(v)}">${fByCur(c, v)}</span>`)
      .join(' ') || fU(0);
  document.getElementById('tSum').innerHTML = `<div class="neo-cell"><div class="l">Open</div><div class="v">${act.length}</div></div>
<div class="neo-cell"><div class="l">Open P&L</div><div class="v">${pnlHtml}</div></div>
<div class="neo-cell"><div class="l">Realized</div><div class="v">${rHtml}</div></div>
<div class="neo-cell"><div class="l">Total Trade P&L</div><div class="v">${totHtml}</div></div>`;
  document.getElementById('tBody').innerHTML =
    act
      .map(t => {
        // === TimeDeposit row ===
        if (t.type === 'TimeDeposit' && t.td) {
          const a = tdAccrued(t.td);
          const cur = t.currency || 'TRY';
          const fCur = cur === 'TRY' ? fT : cur === 'EUR' ? v => '€' + f(v) : fU;
          const isDaily = t.td.kind === 'daily';
          const rate = a ? a.rate.toFixed(2) + '%' : '—';
          const accrued = a ? fCur(a.accruedNet) : '—';
          const kindBadge = isDaily ? 'TD-D' : 'TD-M';
          const hzB = t.horizon || 'short';
          const hzL = { short: 'S', medium: 'M', long: 'L' }[hzB] || 'S';
          const hzC = { short: 'var(--ora)', medium: 'var(--yel)', long: 'var(--pur)' }[hzB] || 'var(--t3)';
          if (isDaily) {
            const rateBtn = `<button class="btn btn-s" style="background:var(--sp-trades);color:#fff;font-size:10px" data-action="changeTdRate" data-args='["${t.id}"]'>Rate</button> `;
            const closeBtn = `<button class="btn btn-s" style="background:var(--yel);color:#000;font-weight:700" data-action="closeDailyTd" data-args='["${t.id}"]'>Close</button> `;
            return `<tr><td><strong>${esc(t.ticker)}</strong>${
              t.td.bank ? `<div style="font-size:9px;color:var(--t3)">${esc(t.td.bank)}</div>` : ''
            }</td><td><span class="badge b-stock">${kindBadge}</span></td><td><span style="font-size:10px;font-weight:600;color:${hzC}">${hzL}</span></td><td>—</td><td>${cur}</td>
<td class="tr">${fCur(t.td.principal)}</td><td class="tr" colspan="2" title="Days open">${a ? a.daysOpen + 'd' : '—'}</td>
<td class="tr" title="Annual rate">${rate}</td><td class="tr pos">${accrued}</td><td class="tr">∞</td>
<td class="tr pos">${a ? fCur(a.currentValue) : '—'}</td>
<td class="tr" colspan="3" style="color:var(--t3);font-size:10px">open-ended</td>
<td><span class="badge b-open">${t.status}</span></td>
<td style="white-space:nowrap">${rateBtn}${closeBtn}<button class="btn btn-s" data-action="editTr" data-args='["${t.id}"]'>Edit</button></td></tr>`;
          }
          const matBtn =
            a && a.matured && !t.td.confirmed
              ? `<button class="btn btn-s" style="background:var(--grn);color:#000;font-weight:700" data-action="confirmMaturity" data-args='["${t.id}"]'>Mature ✓</button> `
              : '';
          const status = a && a.matured ? 'Matured' : t.status;
          const days = a ? `${a.daysToMat}d` : '—';
          const netMat = a ? fCur(a.netMaturity) : '—';
          return `<tr><td><strong>${esc(t.ticker)}</strong>${
            t.td.bank ? `<div style="font-size:9px;color:var(--t3)">${esc(t.td.bank)}</div>` : ''
          }</td><td><span class="badge b-stock">${kindBadge}</span></td><td><span style="font-size:10px;font-weight:600;color:${hzC}">${hzL}</span></td><td>—</td><td>${cur}</td>
<td class="tr">${fCur(t.td.principal)}</td><td class="tr" colspan="2" title="Term">${t.td.termMonths}mo</td>
<td class="tr" title="Effective rate">${rate}</td><td class="tr pos">${accrued}</td><td class="tr">${days}</td>
<td class="tr pos">${netMat}</td>
<td class="tr" colspan="3" style="color:var(--t3);font-size:10px">${a ? a.maturityDate.slice(0, 10) : '—'}</td>
<td><span class="badge b-${status === 'Matured' ? 'closed' : 'open'}">${status}</span></td>
<td style="white-space:nowrap">${matBtn}<button class="btn btn-s" data-action="editTr" data-args='["${t.id}"]'>Edit</button></td></tr>`;
        }
        const p = S.prices[t.ticker],
          c = p ? p.price : null;
        const pnl = c ? (t.side === 'Long' ? c - t.entry : t.entry - c) * t.qty * t.leverage : null;
        const pp =
          t.entry > 0 && c
            ? (((t.side === 'Long' ? c - t.entry : t.entry - c) / t.entry) * 100) * t.leverage
            : null;
        let expPnl = null;
        if (t.annualRate && t.duration) {
          expPnl = t.entry * t.qty * (t.annualRate / 100) * (t.duration / 12);
        } else if (t.tp1) {
          expPnl = (t.side === 'Long' ? t.tp1 - t.entry : t.entry - t.tp1) * t.qty * t.leverage;
        }
        const cur = t.currency || 'USD';
        const fCur =
          cur === 'TRY'
            ? fT
            : cur === 'EUR'
              ? v => (v == null || isNaN(v) ? '—' : '€' + f(v))
              : fU;
        const hzB = t.horizon || 'short';
        const hzL = { short: 'S', medium: 'M', long: 'L' }[hzB] || 'S';
        const hzC = {
          short: 'var(--ora)',
          medium: 'var(--yel)',
          long: 'var(--pur)'
        }[hzB] || 'var(--t3)';
        return `<tr><td><strong>${esc(t.ticker)}</strong></td><td><span class="badge ${bc(t.type)}">${esc(
          t.type
        )}</span></td><td><span style="font-size:10px;font-weight:600;color:${hzC}">${hzL}</span></td><td>${esc(
          t.side
        )}</td><td>${esc(cur)}</td>
<td class="tr">${fCur(t.entry)}</td><td class="tr">${f(t.qty, t.type === 'Crypto' ? 6 : 2)}</td><td class="tr">${
          t.leverage
        }x</td>
<td class="tr">${
          c
            ? `<span style="cursor:pointer" title="Click to edit" data-action="inlineTPrice" data-args='["${t.ticker}",${c}]'>${fCur(
                c
              )}</span>`
            : `<input class="price-input" placeholder="Price..." data-action-change="manTP" data-args='["${t.ticker}"]'>`
        }</td><td class="tr ${pc(pnl)}">${pnl != null ? fCur(pnl) : '—'}</td><td class="tr ${pc(pp)}">${
          pp != null ? fP(pp) : '—'
        }</td>
<td class="tr pos">${expPnl != null ? fCur(expPnl) : '—'}</td>
<td class="tr neg">${t.sl ? fCur(t.sl) : '—'}</td><td class="tr pos">${t.tp1 ? fCur(t.tp1) : '—'}</td><td class="tr pos">${
          t.tp2 ? fCur(t.tp2) : '—'
        }</td>
<td><span class="badge b-${t.status.toLowerCase()}">${t.status}</span></td>
<td style="white-space:nowrap"><button class="btn btn-s" data-action="openAddPosition" data-args='["${t.id}"]'>Add</button> <button class="btn btn-s" data-action="editTr" data-args='["${t.id}"]'>Edit</button> <button class="btn btn-s" data-action="openExit" data-args='["${t.id}"]'>Exit</button></td></tr>`;
      })
      .join('') ||
    '<tr><td colspan="17" class="neo-empty">No active trades — add one with the + button above</td></tr>';
  const d = document.getElementById('proxD');
  if (!act.length) {
    d.innerHTML = '<div class="neo-empty ctr">No active trades</div>';
    return;
  }
  d.innerHTML =
    '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:16px">' +
    act
      .filter(t => t.type !== 'TimeDeposit')
      .map(t => {
        const p = S.prices[t.ticker];
        if (!p) return '';
        const c = p.price;
        const cur = t.currency || 'USD';
        const fc = v => fByCur(cur, v);
        const pnlRaw = (t.side === 'Long' ? c - t.entry : t.entry - c) * t.qty * t.leverage;
        const pnlPct = t.entry > 0 ? (((t.side === 'Long' ? c - t.entry : t.entry - c) / t.entry) * 100) * t.leverage : 0;
        const distP = target => (c && target ? ((target - c) / c) * 100 : null);
        const slD = distP(t.sl),
          t1D = distP(t.tp1),
          t2D = distP(t.tp2);
        // Gauge calc: position current price between SL and TP2 (0-180 degrees)
        const lo = t.sl || t.entry * 0.9,
          hi = t.tp2 || t.tp1 || t.entry * 1.1;
        const range = hi - lo;
        const pct = range > 0 ? Math.max(0, Math.min((c - lo) / range, 1)) : 0.5;
        const angle = pct * 180; // 0=SL side, 180=TP2 side
        // SVG arc gauge
        const cx = 100,
          cy = 95,
          r = 75;
        const toRad = a => (a * Math.PI) / 180;
        const arcX = a => cx + r * Math.cos(toRad(180 - a));
        const arcY = a => cy - r * Math.sin(toRad(180 - a));
        // Background arc (full semi-circle)
        const bgArc = `M ${cx - r},${cy} A ${r},${r} 0 0,1 ${cx + r},${cy}`;
        // Color zones: SL zone (red), neutral (gray), TP zone (green)
        const entryPct = range > 0 ? Math.max(0, Math.min((t.entry - lo) / range, 1)) : 0.5;
        const tp1Pct =
          t.tp1 && range > 0 ? Math.max(0, Math.min((t.tp1 - lo) / range, 1)) : null;
        const entryAngle = entryPct * 180;
        const tp1Angle = tp1Pct !== null ? tp1Pct * 180 : null;
        // Gradient segments for the arc
        const needleA = angle;
        const nX = arcX(needleA),
          nY = arcY(needleA);
        // Build colored arc path
        const arcPath = (startA, endA) => {
          const s = Math.max(0, startA),
            e = Math.min(180, endA);
          if (s >= e) return '';
          const sx = arcX(s),
            sy = arcY(s),
            ex = arcX(e),
            ey = arcY(e);
          const large = e - s > 180 ? 1 : 0;
          return `M ${sx},${sy} A ${r},${r} 0 ${large},0 ${ex},${ey}`;
        };
        const gaugeColor = pnlPct >= 0 ? 'var(--grn)' : 'var(--red)';
        const fillArc = arcPath(0, angle);
        const isPos = pnlPct >= 0;
        return `<div class="gauge-card">
<div class="gc-header"><div><strong style="font-size:15px;color:var(--t1)">${esc(
          t.ticker
        )}</strong><span class="badge ${bc(t.type)}" style="margin-left:6px;font-size:9px">${esc(
          t.type
        )}</span></div><div style="font-size:11px;color:var(--t3)">${esc(t.side)} ${t.leverage}x · ${esc(
          cur
        )}</div></div>
<div class="gc-gauge">
<svg viewBox="0 0 200 110" width="200" height="110">
<path d="${bgArc}" fill="none" stroke="var(--bg0)" stroke-width="14" stroke-linecap="round"/>
${fillArc ? `<path d="${fillArc}" fill="none" stroke="${gaugeColor}" stroke-width="14" stroke-linecap="round" opacity="0.5"/>` : ''}
<path d="${arcPath(Math.max(0, angle - 3), Math.min(180, angle + 3))}" fill="none" stroke="${gaugeColor}" stroke-width="16" stroke-linecap="round"/>
${
  t.sl
    ? `<circle cx="${arcX(0)}" cy="${arcY(0)}" r="4" fill="var(--red)"/><text x="${arcX(0)}" y="${arcY(
        0
      ) + 14}" text-anchor="middle" fill="var(--red)" font-size="8" font-weight="600">SL</text>`
    : ``
}
<circle cx="${arcX(entryAngle)}" cy="${arcY(entryAngle)}" r="3" fill="var(--t3)"/>
<text x="${arcX(entryAngle)}" y="${arcY(entryAngle) + 12}" text-anchor="middle" fill="var(--t3)" font-size="7">ENTRY</text>
${
  tp1Angle !== null
    ? `<circle cx="${arcX(tp1Angle)}" cy="${arcY(tp1Angle)}" r="4" fill="var(--grn)"/><text x="${arcX(
        tp1Angle
      )}" y="${arcY(tp1Angle) + 12}" text-anchor="middle" fill="var(--grn)" font-size="8" font-weight="600">TP1</text>`
    : ``
}
${
  t.tp2
    ? `<circle cx="${arcX(180)}" cy="${arcY(180)}" r="4" fill="var(--grn)" opacity="0.7"/><text x="${arcX(
        180
      )}" y="${arcY(180) + 14}" text-anchor="middle" fill="var(--grn)" font-size="8" opacity="0.7">TP2</text>`
    : ``
}
<circle cx="${nX}" cy="${nY}" r="7" fill="${gaugeColor}" stroke="var(--bg1)" stroke-width="2"/>
</svg>
<div class="gc-price ${isPos ? 'pos' : 'neg'}">${fc(c)}</div>
<div class="gc-pnl"><span class="${pc(pnlRaw)}" style="font-size:16px;font-weight:700">${fc(
          pnlRaw
        )}</span><span class="${pc(pnlPct)}" style="font-size:12px;margin-left:6px">(${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(
          2
        )}%)</span></div>
</div>
<div class="gc-levels">
<div class="gc-lvl gc-sl"><div class="gc-lvl-label">SL</div><div class="gc-lvl-price">${
          t.sl ? fc(t.sl) : '—'
        }</div><div class="gc-lvl-dist ${slD != null && slD < 0 ? 'neg' : 'pos'}">${
          slD != null ? (slD > 0 ? '+' : '') + slD.toFixed(2) + '%' : '—'
        }</div></div>
<div class="gc-lvl gc-entry"><div class="gc-lvl-label">Entry</div><div class="gc-lvl-price">${fc(
          t.entry
        )}</div><div class="gc-lvl-dist ${pc(c - t.entry)}">${
          t.entry ? (((c - t.entry) / t.entry) * 100 >= 0 ? '+' : '') + (((c - t.entry) / t.entry) * 100).toFixed(2) + '%' : '—'
        }</div></div>
<div class="gc-lvl gc-tp1"><div class="gc-lvl-label">TP1</div><div class="gc-lvl-price">${
          t.tp1 ? fc(t.tp1) : '—'
        }</div><div class="gc-lvl-dist pos">${t1D != null ? '+' + t1D.toFixed(2) + '%' : '—'}</div></div>
<div class="gc-lvl gc-tp2"><div class="gc-lvl-label">TP2</div><div class="gc-lvl-price">${
          t.tp2 ? fc(t.tp2) : '—'
        }</div><div class="gc-lvl-dist pos">${t2D != null ? '+' + t2D.toFixed(2) + '%' : '—'}</div></div>
</div>
</div>`;
      })
      .filter(Boolean)
      .join('') +
    '</div>';
  renderTradeJournal();
  try { _trNeo(); } catch (e) { console.error('[ZAGX] _trNeo failed:', e); }
}

/**
 * PART 2b — "What the tape says", one card per open trade the signal feed has an opinion about.
 *
 * ⚠ **THE MOCKUP'S SURFACE DOES NOT EXIST AND THIS IS THE CORRECTION.**
 * `SPEKTRX_SignalStance_Mockup_v0.1.html` drew the stance as a click-to-expand strip inside an
 * Active Trades row. The live Active Trades list is a **17-column `<table id="trTbl">`** with no
 * expandable rows — the mockup borrowed the Journal subpage's `.tjr-*` row, which the Trades page
 * does not have. Rather than add an 18th column to a table that already scrolls, or rebuild the
 * table as cards (the page is untouchable), the same content lands in its own panel directly
 * under Proximity. Both panels answer "which of these needs your attention"; the table stays
 * exactly as it is.
 *
 * ⚠ **THE PANEL HIDES ITSELF WHEN THERE IS NOTHING TO SAY.** Six of his eight open trades have no
 * signal at all. A grey "no signals" chip on every row would be an absence rendered as a
 * measurement — the same failure as back-filling a default (rule #28) — so a trade with no tape
 * gets no card, and when no trade has one the whole panel is `display:none`.
 *
 * Read-only by construction: no button, no `data-action`, nothing that writes. His rule from
 * 2026-08-16 — surface the contradiction, never act on it — after watching one source reverse
 * itself inside ten days on the only ticker it had an opinion about.
 */
function _renderStance(rows, ex) {
  const $ = id => document.getElementById(id);
  const expByKey = {};
  ((ex && ex.rows) || []).forEach(r => { expByKey[r.key] = r; });
  const host = $('trStance'), panel = $('trStancePanel'), nEl = $('trStanceN');
  if (!host || !panel) return;

  const cards = [];
  let conflicted = 0;
  (rows || []).forEach(r => {
    const t = r && r.t;
    // A time deposit has no ticker anyone publishes on; TRYVD2 must never draw a card.
    // ⚠ ON `type`, NOT ONLY ON `r.isTD`. That flag is `type === 'TimeDeposit' && t.td`, and the
    // `&& t.td` is there because the ACCRUAL maths needs the sub-object. This question does not:
    // a deposit row whose `td` never got written is still a deposit, and letting one through
    // BECAUSE it is malformed is exactly backwards. No live instance — all three of his TRYVD2
    // rows carry `td` — found by a fixture that set only the type.
    if (!t || t.type === 'TimeDeposit' || t.isTD || r.isTD) return;
    let st = null;
    try { st = tradeSignalStance(t); } catch (e) { console.warn('[ZAGX] stance failed for', t && t.ticker, e); }
    if (!st) return;
    if (st.verdict === 'conflicted') conflicted++;
    cards.push({ t, st });
  });

  if (!cards.length) { panel.style.display = 'none'; host.innerHTML = ''; return; }
  panel.style.display = '';
  // The badge is the small `.bdg` pill the other panel headers use; it holds a count and nothing
  // else. The conflicted tally goes in the meta line beside it, where the page already puts the
  // sentence-shaped facts.
  if (nEl) nEl.textContent = String(cards.length);
  const meta = document.getElementById('trStanceMeta');
  if (meta) meta.innerHTML = conflicted
    ? `<b style="color:#f5a524">${conflicted} conflicted</b> · reading, not advice — nothing here changes SL, TP or size`
    : 'reading, not advice — nothing here changes SL, TP or size';

  // One comparator, both surfaces — see stanceOrder.
  cards.sort((a, b) => stanceOrder({ st: a.st, ticker: a.t.ticker }, { st: b.st, ticker: b.t.ticker }));

  host.innerHTML = cards.map(({ t, st }) => stanceCard(st, {
    ticker: t.ticker,
    sub: (t.side || '') + (t.currency ? ' \u00b7 ' + t.currency : ''),
    // ⚠ THE SIZE CHIP IS A DECISION, NOT A DECORATION. His call, 2026-08-16: *"hangisi genel
    // SPEKTRX tasarımı açısından uygun ise o olsun."* The design language already answers it —
    // `◆ also an open trade` on the Portfolio card is the same move, and §1.6 of the manifesto
    // says neon is meaning, restrained. So the number appears only on the cards that ask him to
    // act (CONFLICTED / CALL STOOD DOWN) and never on the settled ones, exactly as the overlap
    // segment in the panel below appears only where an overlap exists.
    // ⚠ ONE ACCESSOR, TWO RENDERERS: the figure is `tradeExposure()`'s, not a second sum.
    chip: _sizeChip(expByKey, t.ticker, st.verdict),
    sideNote: `This trade's side reads "${t.side || '(empty)'}", which is neither Long nor Short, `
      + 'so nothing here can be called for or against it.',
  })).join('');
}

/** ⚠ THE APP'S NUMBER FORMAT, NOT THE MOCKUP'S. The mockup drew `11,64%` and `$9.208` because it
 *  was hand-written; every shipped surface renders through `f`/`fU`, which are `en-US`. A panel
 *  that punctuates its numbers differently from the four KPIs above it is a new convention nobody
 *  asked for. One formatter, both renderers. */
const _expPct = v => (v == null ? '\u2014' : f(v) + '%');

/** The chip the stance card carries. Empty string on a settled verdict, and on a ticker the
 *  exposure pass could not price — a chip that says nothing is worse than no chip. */
function _sizeChip(byKey, ticker, verdict) {
  if (verdict !== 'conflicted' && verdict !== 'superseded') return '';
  const r = byKey && byKey[normTicker(ticker)];
  if (!r || r.combinedPct == null) return '';
  return `<span class="stnchip size">\u25c6 ${_expPct(r.combinedPct)} of net worth`
    + `${r.heldUSD > 0 ? ' \u00b7 trade + holding' : ''}</span>`;
}

/**
 * _renderExposure - the Exposure panel (part 3).
 *
 * ⚠ **IT DRAWS `tradeExposure()` AND COMPUTES NOTHING.** Every figure here - the total, the
 * percentages, the overlap - comes from `core/risk-calc.js`. The one thing this function decides
 * is the bar ceiling, and it decides it from the data (see below), because that is presentation.
 *
 * ⚠ **THE CEILING IS DERIVED, NOT 4.** The mockup drew a fixed 4% track because his largest
 * combined name is 3,76% today. A fixed ceiling silently clips the day it is passed, so the track
 * is `max(4, largest rounded up)` - stable at 4 for the book as it stands, and honest after that.
 *
 * ⚠ **AN UNPRICED TRADE IS SAID OUT LOUD.** `unpriced` goes in the meta line. A position missing
 * from a bar chart reads as a smaller book, not as an incomplete one.
 */
function _renderExposure() {
  const $ = id => document.getElementById(id);
  const panel = $('trExpPanel'), host = $('trExp'), head = $('trExpHead'), leg = $('trExpLegend');
  if (!panel || !host) return null;

  let ex = null;
  try { ex = tradeExposure(); } catch (e) { console.warn('[ZAGX] exposure failed', e); }
  if (!ex || !ex.rows.length) {
    panel.style.display = 'none';
    host.innerHTML = ''; if (head) head.innerHTML = ''; if (leg) leg.innerHTML = '';
    return ex;
  }
  panel.style.display = '';

  const nEl = $('trExpN'); if (nEl) nEl.textContent = String(ex.rows.length);
  const meta = $('trExpMeta');
  if (meta) {
    meta.innerHTML = 'at current value \u00b7 against net worth'
      + (ex.unpriced ? ` \u00b7 <b style="color:#f5a524">${ex.unpriced} not priced, not counted</b>` : '');
  }

  if (head) {
    head.innerHTML = `<div><div class="expk">OPEN TRADE EXPOSURE</div><div class="expv">${fU(ex.totalUSD)}</div></div>`
      + `<div><div class="expk">OF NET WORTH</div><div class="expv cy">${_expPct(ex.pctNW)}</div></div>`
      + (ex.largest ? `<div><div class="expk">LARGEST SINGLE NAME</div><div class="expv">${esc(ex.largest.ticker)}`
        + ` <span class="sm">${_expPct(ex.largest.combinedPct)}</span></div></div>` : '');
  }

  const top = Math.max(4, Math.ceil((ex.largest && ex.largest.combinedPct) || 0));
  host.style.setProperty('--expmax', String(top));
  host.innerHTML = ex.rows.map(r => `<div class="expr${r.heldUSD > 0 ? ' ov' : ''}">`
    + `<span class="expt">${esc(r.ticker)}</span>`
    + `<div class="exptr"><i class="a" style="--w:${(r.tradePct || 0).toFixed(2)}"></i>`
    + `${r.heldUSD > 0 ? `<i class="b" style="--w:${(r.heldPct || 0).toFixed(2)}"></i>` : ''}</div>`
    + `<span class="expp">${_expPct(r.combinedPct)}</span>`
    + `<span class="expu">${fU(r.combinedUSD)}</span></div>`).join('');

  // The legend explains the second segment only when a second segment exists - decision 3 of the
  // mockup, and the same rule the panel above follows about absences.
  if (leg) {
    leg.innerHTML = '<span><i class="sw a"></i>the trade</span>'
      + (ex.overlap ? '<span><i class="sw b"></i>the same name held in the portfolio</span>' : '')
      + `<span class="rt">bars are % of net worth, to scale \u00b7 track = ${top}%</span>`;
  }
  return ex;
}

// ===== P4 Trades structure (mockup v1.1, approved 2026-10-17) =====
// Fold state and open quiet rows are properties of the screen, not of the book: a localStorage key
// of their own, never `S`, so they never reach cloud sync, merge or import/export.
export const TR_FOLD_KEY = 'spektrx-trades-folds';
export const TR_FOLDS = ['tape', 'rmult', 'risk', 'exp', 'fx', 'journal', 'eq'];
const _quietOpen = new Set();
function _readFolds() {
  try { const v = JSON.parse(localStorage.getItem(TR_FOLD_KEY) || '[]'); return new Set(Array.isArray(v) ? v.filter(k => TR_FOLDS.includes(k)) : []); } catch (_e) { return new Set(); }
}
function _applyFolds(openSet) {
  document.querySelectorAll('#trNeo [data-fold]').forEach(el => el.classList.toggle('open', openSet.has(el.dataset.fold)));
}
/** trFoldToggle(key) - opens or closes one resting panel; the choice is remembered on this device. */
export function trFoldToggle(key) {
  if (!TR_FOLDS.includes(key)) return;
  const open = _readFolds(); if (open.has(key)) open.delete(key); else open.add(key);
  try { localStorage.setItem(TR_FOLD_KEY, JSON.stringify([...open])); } catch (_e) { /* private mode: lasts for this page */ }
  _applyFolds(open);
}
/** trQuietToggle(id) - opens or closes one row of the quiet table (not persisted; rows change). */
export function trQuietToggle(id) {
  if (_quietOpen.has(id)) _quietOpen.delete(id); else _quietOpen.add(id);
  document.querySelectorAll('#trQuiet .qr').forEach(row => { if (row.dataset.tid === String(id)) row.classList.toggle('open', _quietOpen.has(id)); });
}
/** _trFolds - one line per resting panel: its key figure and a mark drawn from the panel's own render. */
function _trFolds(figs = {}) {
  const $ = id => document.getElementById(id);
  const txt = sel => (document.querySelector(sel)?.textContent || '').replace(/\s+/g, ' ').trim();
  const bars = vals => vals.length ? '<span class="mbars">' + (() => { const mx = Math.max(...vals.map(Math.abs), 1e-9); return vals.map(v => `<i style="height:${Math.max(8, Math.abs(v) / mx * 100).toFixed(0)}%"></i>`).join(''); })() + '</span>' : '';
  const set = (key, fig, mark) => { const fv = $('trFv-' + key), fm = $('trFm-' + key); if (fv) fv.textContent = fig || ''; if (fm) fm.innerHTML = mark || ''; };
  set('tape', txt('#trStanceN') ? txt('#trStanceN') + ' reading' + (txt('#trStanceN') === '1' ? '' : 's') : '', '');
  // figures come from the render's own variables, not from re-reading the text they produced
  const rm = (figs.rm || []).slice().sort((a, b) => b[1] - a[1]);
  set('rmult', rm.length ? `best ${esc(rm[0][0])} ${rm[0][1].toFixed(2)}R · ${rm.length} position${rm.length === 1 ? '' : 's'}` : 'no priced positions', bars(rm.map(x => x[1])));
  const atRisk = figs.totRisk || 0, rew = figs.totReward || 0;
  set('risk', atRisk > 0 ? `${fU(atRisk)}${figs.pctNW != null ? ' · ' + figs.pctNW.toFixed(1) + '% of net worth' : ''}` : '', (atRisk > 0 && rew > 0) ? `<span class="mseg two"><i class="risk" style="width:${(atRisk / (atRisk + rew) * 100).toFixed(1)}%"></i><i class="rew" style="width:${(rew / (atRisk + rew) * 100).toFixed(1)}%"></i></span>` : '');
  set('exp', [...document.querySelectorAll('#trExpHead .expv')].slice(0, 2).map(e => e.textContent.trim()).join(' · '), bars([...document.querySelectorAll('#trExp .exptr i')].map(e => parseFloat(e.style.getPropertyValue('--w')) || 0)));
  set('fx', [...document.querySelectorAll('#trFx .fxrow')].map(r => (r.querySelector('.fxlbl')?.textContent.trim() || '') + ' ' + (r.querySelector('.fxval')?.textContent.trim().split('·')[0].trim() || '')).join(' · '), '<span class="mseg">' + [...document.querySelectorAll('#trFx .fxfill')].map(e => `<i style="width:${e.style.width};background:${e.style.background}"></i>`).join('') + '</span>');
  // the dots are the trades the figure counts (realizedOrigin 'trade'), newest last
  const jr = (S.realized || []).filter(r => realizedOrigin(r) === 'trade').slice().sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
  set('journal', txt('#trK3s'), '<span class="mdots">' + jr.map(r => `<i class="${realizedUSD(r) >= 0 ? 'w' : 'l'}"></i>`).join('') + '</span>');
  const eq = $('trEq'); let meq = '';
  if (eq && eq.innerHTML) { const c = eq.cloneNode(true); c.removeAttribute('id'); c.setAttribute('width', '120'); c.setAttribute('height', '24'); c.setAttribute('class', 'meq'); c.querySelectorAll('[id]').forEach(n => n.removeAttribute('id')); c.querySelectorAll('defs, path[fill^="url"]').forEach(n => n.remove()); meq = c.outerHTML; }
  set('eq', [...document.querySelectorAll('#trEqHead > div')].map(e => e.textContent.trim()).join(' · '), meq);
  _applyFolds(_readFolds());
}

/** _trNeo - feeds neon Trades (#trNeo) from live data. Legacy (#trLegacy) hidden; connections preserved. */
function _trNeo() {
  const $ = id => document.getElementById(id);
  if (!$('trBody')) return;
  const fc = (v, cur) => v == null ? '—' : fByCur(cur || 'USD', v);
  const open = S.trades.filter(t => t.status !== 'Closed');
  const rows = open.map(t => {
    const cur = t.currency || 'USD';
    const lev = t.leverage || 1;
    const p = S.prices[t.ticker]; const price = p ? p.price : null;
    const isTD = (t.type === 'TimeDeposit' && t.td);
    let pnlUSD = 0, pnlP = null, pnlLocal = null, expLocal = null;
    if (isTD) {
      const a = tdAccrued(t.td);
      pnlLocal = a ? (a.accruedNet || 0) : 0;           // accrued interest so far, in TD currency
      pnlUSD = toUSD(pnlLocal, cur);
      pnlP = (a && t.td.principal) ? (pnlLocal / t.td.principal * 100) : null;
    } else if (price != null) {
      const move = t.side === 'Long' ? (price - t.entry) : (t.entry - price);
      pnlLocal = move * t.qty * lev;                     // realized-if-closed-now P&L, in trade currency
      pnlUSD = toUSD(pnlLocal, cur);
      pnlP = t.entry ? (move / t.entry * 100) : null;
      if (t.tp1 != null) {                               // expected P&L if TP1 is hit
        const tmove = t.side === 'Long' ? (t.tp1 - t.entry) : (t.entry - t.tp1);
        expLocal = tmove * t.qty * lev;
      }
    }
    // No SL set -> the shared default, which is leverage-aware: min(25%, 70%/lev). See
    // HORIZ-002 in core/risk-calc.js — this was one of five hand-written copies.
    const slEff = effectiveStop(t);
    const isDefSL = t.sl == null;
    const floor = slEff;
    const prox = (t.tp1 != null && price != null && floor != null && (t.tp1 - floor) !== 0) ? Math.max(0, Math.min((price - floor) / (t.tp1 - floor), 1)) : null;
    const toTP1 = (t.tp1 != null && price != null && price !== 0) ? (t.tp1 - price) / price * 100 : null;
    return { t, cur, lev, price, isTD, pnlUSD, pnlP, pnlLocal, expLocal, slEff, isDefSL, prox, toTP1, floor };
  });
  // KPIs
  const openPnL = rows.reduce((s, r) => s + r.pnlUSD, 0);
  // NOTE: local must NOT be named `realizedUSD` — it would shadow the imported
  // realizedUSD() helper (live regression 2026-07-13: KPIs froze at 0).
  // Trading-scoped: TD interest (kind:'interest') is excluded from realized P&L
  // and win-rate — it is guaranteed-positive yield, not a directional trade
  // outcome. It still counts in total earnings (Goals) and cash proceeds (Cash).
  let realizedTot = 0, wins = 0, tot = 0;
  // ⚠ THE TRADES PAGE COUNTS TRADES. `S.realized` is the whole book's ledger and 13 of his 21
  // rows are portfolio `Exit Holding` exits, so counting all of them made "win rate" a statement
  // about rebalancing: 90 % over 21, against 100 % over 7 trades. He caught it 2026-08-16 and
  // chose the split. `realizedOrigin` is the one definition; the journal reads the same function.
  // Portfolio P&L is not hidden — it keeps its rows and its own total on the Journal subpage.
  let pfTot = 0, pfN = 0;
  (S.realized || []).forEach(r => {
    const o = realizedOrigin(r);
    if (o === 'interest') return;
    const v = realizedUSD(r);
    if (o === 'portfolio') { pfTot += v; pfN++; return; }
    realizedTot += v; tot++; if (v > 0) wins++;
  });
  $('trK1').textContent = open.length;
  // trK2 remapped to TD Accrued (Heroes Mockup v0.1, approved 2026-07-14 — Open P&L moved to hero headline)
  const tdRows = rows.filter(r => r.isTD);
  const tdAccUSD = tdRows.reduce((s, r) => s + r.pnlUSD, 0);
  const k2 = $('trK2'); k2.className = 'v ' + pc(tdAccUSD); k2.textContent = (tdAccUSD >= 0 ? '+' : '') + fU(tdAccUSD);
  const k3 = $('trK3'); k3.className = 'v ' + pc(realizedTot); k3.textContent = (realizedTot >= 0 ? '+' : '') + fU(realizedTot);
  $('trK4').textContent = tot ? Math.round(wins / tot * 100) + '%' : '—';
  // header meta + KPI story sub-lines (same numbers, presentation only)
  const levCount = rows.filter(r => r.lev > 1).length;
  const noSL = rows.filter(r => r.isDefSL && !r.isTD).length;
  const tm = $('trMeta');
  // "win rate" is over TRADES; the portfolio exits are counted beside it, never inside it.
  if (tm) tm.innerHTML = `<b>${open.length}</b> open · <b class="${openPnL >= 0 ? 'pos' : 'neg'}">${openPnL >= 0 ? '+' : ''}${fU(openPnL)}</b> unrealized · win rate ${tot ? Math.round(wins / tot * 100) + '%' : '—'}`
    + `${pfN ? ` · <span style="color:var(--t3)">${pfN} portfolio exit${pfN === 1 ? '' : 's'} ${pfTot >= 0 ? '+' : ''}${fU(pfTot)}</span>` : ''}`;
  const sub = (id, txt) => { const e = $(id); if (e) e.textContent = txt; };
  sub('trK1s', `${levCount} leveraged${noSL ? ` · ${noSL} on default SL` : ''}`);
  sub('trK2s', tdRows.length ? `${tdRows.length} time deposit${tdRows.length === 1 ? '' : 's'} accruing` : 'no time deposits');
  // ⚠ NAME THE POPULATION. The tile says "Realized P&L" and now counts trades only, so the
  // sub-line has to say which rows are in it and where the rest went — otherwise the number
  // silently got smaller and nothing explains why.
  sub('trK3s', `${tot} trade${tot === 1 ? '' : 's'} · ${wins} wins`
    + (pfN ? ` · ${pfN} portfolio exit${pfN === 1 ? '' : 's'} ${pfTot >= 0 ? '+' : ''}${fU(pfTot)}` : ''));
  sub('trK4s', tot ? `${wins}/${tot} profitable trades` : 'no closed trades yet');
  // proximity rail — top 5 by default; rest behind a "show all" expander (progressive disclosure)
  //
  // ⚠ A BREACHED STOP USED TO SORT LAST. `prox` is clamped — `Math.max(0, ...)` where it is
  // computed — so every position past its stop lands on exactly 0, and a descending sort put the
  // most urgent rows at the BOTTOM, behind the "show all" expander. Measured on the live book
  // 2026-10-02: ZOREN sat 24% under its stop and rendered identically to a position sitting
  // exactly ON it, five rows down and collapsed.
  //
  // That mattered because the alert cannot cover for it. `scanTradeCrossings` fires on the
  // CROSSING only, and `alertedKeys` is pruned at 30 days (state.js:1155) — ZOREN's SL alert
  // fired on 08-31, its key has since been pruned, and its price has been under the stop
  // continuously, so it can neither re-fire nor show that it fired. The event expired; the fact
  // did not. This rail is where the fact has to live, so a breach now leads it, deepest first.
  //
  // `buf` is the number already on screen and is now also the sort key, so the ordering and the
  // label can never disagree. Breach is measured against `slEff`, the level this rail DRAWS,
  // which includes the leverage-aware default for a trade that declared no stop. `integrity.js`
  // (`trades.beyondStop`) scopes to an explicit `t.sl` instead, because that is the only level
  // `scanTradeCrossings` alerts on. Same word, two scopes, both deliberate.
  //
  // `buf` and `hit` below are Long-shaped and stay that way: all 22 trades this book has ever
  // held are Long, so a side-aware rewrite here would be an untested path invented on a hunch.
  // The integrity check does handle Short, because there a comparison is all it costs.
  const _bufPct = r => (r.price && r.slEff != null) ? (r.price - r.slEff) / r.price * 100 : null;
  const allProx = rows.filter(r => r.prox != null).sort((a, b) => {
    const ab = (_bufPct(a) || 0) < 0, bb = (_bufPct(b) || 0) < 0;
    if (ab !== bb) return ab ? -1 : 1;
    if (ab && bb) return (_bufPct(a) || 0) - (_bufPct(b) || 0);   // deepest breach leads
    return b.prox - a.prox;
  });
  const top5 = allProx.slice(0, 5);
  const rest = allProx.slice(5);
  const proxRow = (r, curId) => {
    const t = r.t; const floor = r.floor; const dom = t.tp1 - floor; const pos = v => Math.max(0, Math.min((v - floor) / dom * 100, 100));
    const enPos = pos(t.entry); const buf = _bufPct(r); const hit = r.price >= t.tp1;
    const past = buf != null && buf < 0;
    const slLab = `SL ${fc(r.slEff, t.currency)}${r.isDefSL ? ' ·def' : ''}`;
    return `<div class="pr${past ? ' pr-past' : ''}">
      <div class="pid"><div class="t"><span class="tk" style="width:24px;height:24px;font-size:9px">${esc((t.ticker || '').slice(0, 3))}</span>${esc(t.ticker)}</div><div class="s">${esc(t.side)} · now ${fc(r.price, t.currency)}</div></div>
      <div class="track2"><div class="rail"></div>
        <div class="tick sl${r.isDefSL ? ' def' : ''}" style="left:0"></div><div class="lab${r.isDefSL ? ' def' : ''}" style="left:0;transform:none">${slLab}</div>
        <div class="tick en" style="left:${enPos.toFixed(1)}%"></div><div class="lab" style="left:${enPos.toFixed(1)}%">entry</div>
        <div class="tick tp" style="left:100%"></div><div class="lab" style="left:100%;transform:translateX(-100%)">TP1 ${fc(t.tp1, t.currency)}</div>
        <div class="cur" id="${curId}" style="left:0"></div>
      </div>
      <div class="stat">${hit ? '<div class="big pos">TP1 ✓</div><div class="sm">hit</div>' : `<div class="big pos">+${(r.toTP1 || 0).toFixed(1)}%</div><div class="sm">to TP1</div>`}<div class="sm${past ? ' neg' : ''}">${past
        ? `<b>PAST SL</b> · ${Math.abs(buf).toFixed(1)}% beyond${r.isDefSL ? ' (def)' : ''}`
        : `buf ${buf != null ? buf.toFixed(1) : '—'}% › SL${r.isDefSL ? ' (def)' : ''}`}</div></div>
    </div>`;
  };
  $('trProx').innerHTML = top5.map((r, i) => proxRow(r, 'trCur' + i)).join('') || '<div class="neo-empty">No open trades with TP1 set</div>';
  const proxDet = $('trProxDetails'), proxMore = $('trProxMore'), proxN = $('trProxN');
  if (proxDet) {
    if (rest.length) { proxDet.style.display = ''; if (proxN) proxN.textContent = '(' + rest.length + ')'; if (proxMore) proxMore.innerHTML = rest.map((r, i) => proxRow(r, 'trCurM' + i)).join(''); }
    else { proxDet.style.display = 'none'; if (proxMore) proxMore.innerHTML = ''; }
  }
  requestAnimationFrame(() => requestAnimationFrame(() => {
    top5.forEach((r, i) => { const c = $('trCur' + i); if (c) c.style.left = (r.prox * 100).toFixed(1) + '%'; });
    rest.forEach((r, i) => { const c = $('trCurM' + i); if (c) c.style.left = (r.prox * 100).toFixed(1) + '%'; });
  }));
  // Exposure first: the stance cards below borrow its figures for the size chip, and a chip
  // drawn from a stale pass would be a number with two owners.
  const _ex = _renderExposure();
  _renderStance(rows, _ex);
  // ===== R-Multiple strip + Open Risk gauge (High widgets) =====
  const rm = rows.filter(r => !r.isTD && r.t.tp1 != null && r.price != null && r.slEff != null).map(r => {
    const t = r.t; const riskUnit = Math.abs(t.entry - r.slEff);
    const Rtp = riskUnit ? (Math.abs(t.tp1 - t.entry) / riskUnit) : null;
    const curR = riskUnit ? ((t.side === 'Long' ? r.price - t.entry : t.entry - r.price) / riskUnit) : null;
    const riskUSD = toUSD(riskUnit * t.qty * r.lev, r.cur);
    const rewardUSD = toUSD(Math.abs(t.tp1 - t.entry) * t.qty * r.lev, r.cur);
    return { tk: t.ticker, Rtp, curR, riskUSD, rewardUSD };
  }).filter(x => x.Rtp != null);
  const rmEl = $('trRmult');
  if (rmEl) {
    if (!rm.length) rmEl.innerHTML = '<div class="neo-empty">No open trades with TP1 set</div>';
    else {
      rm.sort((a, b) => b.Rtp - a.Rtp);
      const maxR = Math.max(...rm.map(r => r.Rtp)) * 1.05 || 1;
      const zero = 10, spanw = 88;
      rmEl.innerHTML = rm.map(r => {
        const tpPos = Math.min(zero + (r.Rtp / maxR) * spanw, 98);
        const curClamp = Math.max(-0.5, Math.min(r.curR, r.Rtp));
        // floor at 0 — same track-overflow fix as risk.js (fill bled over ticker labels)
        const curPos = Math.max(0, zero + (curClamp / maxR) * spanw);
        const col = r.curR >= 0 ? 'var(--grn)' : 'var(--red)';
        const left = r.curR >= 0 ? zero : Math.min(zero, curPos);
        const width = Math.abs(curPos - zero);
        return `<div class="rmrow"><div class="rmtk">${esc(r.tk)}<span>now ${r.curR >= 0 ? '+' : ''}${r.curR.toFixed(2)}R</span></div><div class="rmtrack"><div class="rmfill" style="left:${left}%;width:${width}%;background:${col}"></div><div class="rmzero" style="left:${zero}%"></div><div class="rmtp" style="left:${tpPos}%">▸TP1</div></div><div class="rmval">${r.Rtp.toFixed(2)}R</div></div>`;
      }).join('');
    }
  }
  // Risk aggregates lifted out of the panel render — hero ring reuses them (single computation)
  const totRisk = rm.reduce((s, r) => s + r.riskUSD, 0);
  const totReward = rm.reduce((s, r) => s + r.rewardUSD, 0);
  // LIVE net worth through the ONE accessor. This was a private chain -- __computeTW, then
  // __twUSD, then 0 -- written 2026-07-14 (cold boot showed 2112% off a default-state $2,400)
  // when registerNetWorth did not yet exist. It stayed a fourth route to a quantity N1 grades to
  // one, and its terminal fallback of 0 silently dropped the "% of net worth" clause where
  // risk-calc would have answered. HORIZ-007.
  const nw = netWorthUSD();
  const pctNW = nw ? (totRisk / nw * 100) : null;
  const riskEl = $('trRisk');
  if (riskEl) {
    const rr = totRisk ? (totReward / totRisk) : 0;
    const totRR = (totRisk + totReward) || 1;
    riskEl.innerHTML = `<div class="riskNum">${fU(totRisk)}</div><div class="riskSub">total at risk${pctNW != null ? ` · <b class="neg">${pctNW.toFixed(1)}%</b> of net worth` : ''}</div>
      <div class="rrbar"><div style="width:${(totRisk / totRR * 100).toFixed(1)}%;background:var(--red)"></div><div style="width:${(totReward / totRR * 100).toFixed(1)}%;background:var(--grn)"></div></div>
      <div class="riskKpi"><div class="rk"><div class="l">At risk</div><div class="v neg">${fU(totRisk)}</div></div><div class="rk"><div class="l">Reward @TP1</div><div class="v pos">${fU(totReward)}</div></div><div class="rk"><div class="l">R:R</div><div class="v" style="color:var(--cyan)">${rr.toFixed(2)}</div></div></div>`;
  }
  // ===== Hero (Heroes Mockup v0.1, approved 2026-07-14) — Open P&L headline + risk-used ring =====
  const thn = $('trHeroNum');
  if (thn) {
    thn.className = 'neo-hnum ' + pc(openPnL);
    thn.textContent = (openPnL >= 0 ? '+' : '') + fU(openPnL);
    $('trHeroChips').innerHTML =
      `<span class="neo-chip">Open <b class="hx">${open.length}</b>${levCount ? ` · ${levCount} leveraged` : ''}</span>` +
      `<span class="neo-chip">Realized <b class="${realizedTot >= 0 ? 'pos' : 'neg'}">${realizedTot >= 0 ? '+' : ''}${fU(realizedTot)}</b></span>` +
      `<span class="neo-chip">Win rate <b>${tot ? Math.round(wins / tot * 100) + '%' : '—'}</b></span>` +
      (totRisk > 0 ? `<span class="neo-chip">Open risk <b class="neg">${fU(totRisk)}</b></span>` : '');
    // context: closest TP1 trigger + tightest SL buffer (same formulas as proximity rail)
    const closest = allProx.find(r => r.toTP1 != null && r.price < r.t.tp1);
    const hitN = allProx.filter(r => r.price != null && r.t.tp1 != null && r.price >= r.t.tp1).length;
    const bufRows = rows.filter(r => !r.isTD && r.price != null && r.slEff != null)
      .map(r => ({ tk: r.t.ticker, buf: (r.price - r.slEff) / r.price * 100 })).sort((a, b) => a.buf - b.buf);
    const tight = bufRows[0];
    $('trHeroCtx').innerHTML = open.length
      ? [
        hitN ? `<b class="pos">${hitN} at TP1 ✓</b>` : '',
        closest ? `Closest trigger: <b>${esc(closest.t.ticker)}</b> <b class="pos">${(closest.toTP1 || 0).toFixed(1)}% to TP1</b>` : '',
        tight ? `tightest stop: ${esc(tight.tk)} buf ${tight.buf.toFixed(1)}% › SL` : '',
        pctNW != null ? `open risk = <b class="neg">${pctNW.toFixed(1)}%</b> of net worth` : ''
      ].filter(Boolean).join(' · ') || 'Open trades have no TP/SL levels set.'
      : 'No open trades — journal and realized stats stay below.';
    // ring: risk used vs budget (settable S.settings.riskBudgetUSD; 0/empty = auto 5% of net worth)
    const budget = (S.settings?.riskBudgetUSD > 0) ? S.settings.riskBudgetUSD : (nw ? nw * 0.05 : 0);
    const ring = $('trHeroRing'), rp = $('trHeroPct');
    if (budget > 0) {
      const used = totRisk / budget * 100;
      if (ring) requestAnimationFrame(() => requestAnimationFrame(() => { ring.style.strokeDashoffset = (323 * (1 - Math.min(used, 100) / 100)).toFixed(1); }));
      if (rp) { rp.textContent = Math.round(used) + '%'; rp.style.color = used > 100 ? 'var(--red)' : ''; }
    } else {
      if (ring) ring.style.strokeDashoffset = 323;
      if (rp) rp.textContent = '—';
    }
  }
  // ===== Realized equity curve (Medium widget) =====
  const eqEl = $('trEq'), eqHead = $('trEqHead');
  if (eqEl) {
    const re = (S.realized || []).filter(r => !isInterestRealized(r)).map(r => ({ d: r.date, v: realizedUSD(r) })).filter(x => x.d).sort((a, b) => new Date(a.d) - new Date(b.d));
    let c = 0; const pts = [0]; re.forEach(x => { c += x.v; pts.push(c); });
    const total = c;
    if (eqHead) eqHead.innerHTML = `<div class="${total >= 0 ? 'pos' : 'neg'}" style="font-family:var(--mono);font-size:24px;font-weight:700">${total >= 0 ? '+' : ''}${fU(total)}</div><div style="font-size:11px;color:var(--t3)">${re.length} closed</div>`;
    if (pts.length < 2) { eqEl.innerHTML = ''; }
    else {
      const W = 520, Hh = 120, pad = 6; const mx = Math.max(...pts, 0.01), mn = Math.min(...pts, 0); const rng = (mx - mn) || 1;
      const sx = i => pad + i / (pts.length - 1) * (W - 2 * pad); const sy = v => Hh - pad - ((v - mn) / rng) * (Hh - 2 * pad);
      let d = 'M' + sx(0).toFixed(1) + ' ' + sy(pts[0]).toFixed(1); pts.forEach((v, i) => { if (i) d += ' L' + sx(i).toFixed(1) + ' ' + sy(v).toFixed(1); });
      const zeroY = sy(0).toFixed(1);
      const area = d + ` L${sx(pts.length - 1).toFixed(1)} ${zeroY} L${sx(0).toFixed(1)} ${zeroY} Z`;
      eqEl.innerHTML = `<defs><linearGradient id="eqg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#00f0ff" stop-opacity=".28"/><stop offset="1" stop-color="#00f0ff" stop-opacity="0"/></linearGradient></defs><path d="${area}" fill="url(#eqg)"/><path d="${d}" fill="none" stroke="#00f0ff" stroke-width="2"/>`;
    }
  }
  // ===== Realized by month (P4). Same population as the equity curve, by construction: it is
  // drawn from the `re` array the curve was just drawn from, so the two totals cannot disagree.
  const monEl = $('trMon');
  const r_isDate = d => d instanceof Date && !isNaN(d.getTime());
  if (monEl && eqEl) {
    // the month key comes from the same Date parse the curve sorts by, so a row the curve counts
    // lands in a month here; a row with an unparseable date is excluded from BOTH counts below
    const re = (S.realized || []).filter(r => !isInterestRealized(r)).map(r => ({ d: new Date(r.date), v: realizedUSD(r) })).filter(x => r_isDate(x.d));
    const byM = {}; re.forEach(x => { const k = x.d.getUTCFullYear() + '-' + String(x.d.getUTCMonth() + 1).padStart(2, '0'); (byM[k] = byM[k] || { p: 0, n: 0 }); byM[k].p += x.v; byM[k].n++; });
    const keys = Object.keys(byM).sort();
    const monHead = $('trMonHead');
    if (!keys.length) { monEl.innerHTML = '<div class="neo-empty">No closed trades yet.</div>'; if (monHead) monHead.textContent = ''; }
    else {
      const MN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      let [y, m] = keys[0].split('-').map(Number); const [ly, lm] = keys[keys.length - 1].split('-').map(Number);
      const months = []; while (y < ly || (y === ly && m <= lm)) { months.push(`${y}-${String(m).padStart(2, '0')}`); m++; if (m > 12) { m = 1; y++; } if (months.length > 240) break; }
      const mx = Math.max(...keys.map(k => Math.abs(byM[k].p)), 1e-9);
      const total = re.reduce((a, x) => a + x.v, 0);
      const best = keys.slice().sort((a, b) => byM[b].p - byM[a].p)[0];
      const sgn = v => (v >= 0 ? '+' : '') + fU(v);   // the equity head's own form
      monEl.classList.toggle('dense', months.length > 12);
      monEl.innerHTML = months.map(k => { const v = byM[k]; const mm = +k.slice(5) - 1; const h = v ? Math.max(3, Math.abs(v.p) / mx * 100) : 0;
        return `<div class="mc${v ? (v.p >= 0 ? ' g' : ' r') : ''}" title="${k} · ${v ? v.n + ' close' + (v.n > 1 ? 's' : '') + ' · ' + sgn(v.p) : 'no closes'}"><div class="mv">${v ? sgn(v.p) : ''}</div><div class="mb"><i style="height:${h.toFixed(1)}%"></i></div><div class="ml">${MN[mm]}${(mm === 0 || k === months[0]) ? ` <span>${k.slice(2, 4)}</span>` : ''}</div><div class="mn">${v ? v.n : ''}</div></div>`; }).join('');
      const mLab = k => MN[+k.slice(5) - 1] + ' ' + k.slice(0, 4);
      const first = months[0], last = months[months.length - 1];
      const range = first === last ? mLab(first) : (first.slice(0, 4) === last.slice(0, 4) ? `${MN[+first.slice(5) - 1]}–${mLab(last)}` : `${mLab(first)} – ${mLab(last)}`);
      if (monHead) monHead.textContent = `${re.length} close${re.length === 1 ? '' : 's'} · ${range} · ${sgn(total)} · best ${mLab(best)} ${sgn(byM[best].p)}`;
    }
  }
  // ===== Currency exposure (holdings + cash + open trade capital → USD) =====
  const fxEl = $('trFx');
  if (fxEl) {
    // All four legs -- holdings, cash, open-trade capital, then the sorted breakdown -- come from
    // riskFxExposure(). They were re-implemented verbatim here while risk-calc's own docblock said
    // "Matches trades #trFx", and the two disagreed about their route to the TRY leg: _cashTRY()
    // against a direct cashTRYNow(), equal only because main.js:44 wires the provider. HORIZ-006.
    const col = { USD: '#6aa0ff', TRY: '#34e2a0', EUR: '#a78bfa' };
    fxEl.innerHTML = riskFxExposure().breakdown.map(({ ccy, usd, pct }) =>
      `<div class="fxrow"><div class="fxlbl">${ccy}</div><div class="fxtrack"><div class="fxfill" style="width:${pct.toFixed(1)}%;background:${col[ccy] || '#9aa6c7'}"></div></div><div class="fxval">${pct.toFixed(0)}% · ${fU(usd)}</div></div>`).join('');
  }
  // active table
  $('trCnt').textContent = open.length;
  $('trBody').innerHTML = rows.map(r => {
    const t = r.t; const cur = r.cur;
    const fCur = cur === 'TRY' ? fT : cur === 'EUR' ? (v => v == null || isNaN(v) ? '—' : '€' + f(v)) : fU;
    const hzB = t.horizon || 'short';
    const hzL = { short: 'S', medium: 'M', long: 'L' }[hzB] || 'S';
    const hzC = { short: 'var(--ora)', medium: 'var(--yel)', long: 'var(--pur)' }[hzB] || 'var(--t3)';
    const tkChip = `<span class="tk">${esc((t.ticker || '').slice(0, 3))}</span>`;
    // ===== TimeDeposit rows (mirror legacy layout + full actions) =====
    if (r.isTD) {
      const a = tdAccrued(t.td);
      const isDaily = t.td.kind === 'daily';
      const rate = a ? a.rate.toFixed(2) + '%' : '—';
      const accrued = a ? fCur(a.accruedNet) : '—';
      const kindBadge = isDaily ? 'TD-D' : 'TD-M';
      const nameSub = t.td.bank ? `<div style="font-size:9px;color:var(--t3)">${esc(t.td.bank)}</div>` : '';
      const head = `<td class="txt"><span style="display:flex;align-items:center;gap:8px">${tkChip}<b>${esc(t.ticker)}</b></span>${nameSub}</td>
        <td class="txt"><span class="badge b-wait">${kindBadge}</span></td>
        <td><span style="font-size:10px;font-weight:600;color:${hzC}">${hzL}</span></td>
        <td style="color:var(--t3)">—</td><td>${cur}</td>
        <td>${fCur(t.td.principal)}</td>`;
      if (isDaily) {
        const rateBtn = `<button class="glassbtn" data-action="changeTdRate" data-args='["${t.id}"]' style="padding:3px 8px">Rate</button> `;
        const closeBtn = `<button class="glassbtn" data-action="closeDailyTd" data-args='["${t.id}"]' style="padding:3px 8px;color:var(--yel)">Close</button> `;
        return `<tr>${head}
        <td colspan="2" style="color:var(--t3)" title="Days open">${a ? a.daysOpen + 'd' : '—'}</td>
        <td title="Annual rate">${rate}</td>
        <td class="pos">${accrued}</td>
        <td style="color:var(--t3)">∞</td>
        <td class="pos">${a ? fCur(a.currentValue) : '—'}</td>
        <td colspan="3" style="color:var(--t3);font-size:10px">open-ended</td>
        <td class="txt"><span class="badge b-buy">${esc(t.status)}</span></td>
        <td style="white-space:nowrap">${rateBtn}${closeBtn}<button class="glassbtn" data-action="editTr" data-args='["${t.id}"]' style="padding:3px 8px">Edit</button></td></tr>`;
      }
      const matBtn = (a && a.matured && !t.td.confirmed)
        ? `<button class="glassbtn" data-action="confirmMaturity" data-args='["${t.id}"]' style="padding:3px 8px;color:var(--grn)">Mature ✓</button> ` : '';
      const status = a && a.matured ? 'Matured' : t.status;
      const days = a ? `${a.daysToMat}d` : '—';
      const netMat = a ? fCur(a.netMaturity) : '—';
      return `<tr>${head}
        <td colspan="2" style="color:var(--t3)" title="Term">${t.td.termMonths}mo</td>
        <td title="Effective rate">${rate}</td>
        <td class="pos">${accrued}</td>
        <td style="color:var(--t3)">${days}</td>
        <td class="pos">${netMat}</td>
        <td colspan="3" style="color:var(--t3);font-size:10px">${a ? a.maturityDate.slice(0, 10) : '—'}</td>
        <td class="txt"><span class="badge ${status === 'Matured' ? 'b-sell' : 'b-buy'}">${status}</span></td>
        <td style="white-space:nowrap">${matBtn}<button class="glassbtn" data-action="editTr" data-args='["${t.id}"]' style="padding:3px 8px">Edit</button></td></tr>`;
    }
    // ===== Stock / Crypto / Commodity rows =====
    const st = t.status || 'Open';
    const priceCell = r.price != null
      ? `<span style="cursor:pointer" title="Click to edit" data-action="inlineTPrice" data-args='["${t.ticker}",${r.price}]'>${fCur(r.price)}</span>`
      : `<input class="price-input" placeholder="Price..." data-action-change="manTP" data-args='["${t.ticker}"]'>`;
    const pnlCell = r.pnlLocal != null
      ? `<span class="${pc(r.pnlLocal)}">${r.pnlLocal >= 0 ? '+' : ''}${fCur(r.pnlLocal)}</span><span style="color:var(--t3);font-size:10px;display:block">${r.pnlUSD >= 0 ? '+' : ''}${fU(r.pnlUSD)}</span>`
      : '<span style="color:var(--t3)">—</span>';
    const slCell = t.sl != null
      ? `<span class="neg">${fCur(t.sl)}</span>`
      : `<span class="neg" style="opacity:.6" title="default SL 25% from entry">${fCur(r.slEff)}·</span>`;
    return `<tr>
      <td class="txt"><span style="display:flex;align-items:center;gap:8px">${tkChip}<b>${esc(t.ticker)}</b></span></td>
      <td class="txt"><span class="badge ${bc(t.type)}">${esc(t.type)}</span></td>
      <td><span style="font-size:10px;font-weight:600;color:${hzC}">${hzL}</span></td>
      <td class="txt"><span class="badge ${t.side === 'Long' ? 'b-buy' : 'b-sell'}">${esc(t.side)}</span></td>
      <td>${esc(cur)}</td>
      <td>${fCur(t.entry)}</td>
      <td>${f(t.qty, t.type === 'Crypto' ? 6 : 2)}</td>
      <td style="color:var(--t3)">${r.lev}×</td>
      <td>${priceCell}</td>
      <td>${pnlCell}</td>
      <td>${r.pnlP != null ? `<span class="${pc(r.pnlP)}">${fP(r.pnlP)}</span>` : '<span style="color:var(--t3)">—</span>'}</td>
      <td class="pos">${r.expLocal != null ? fCur(r.expLocal) : '—'}</td>
      <td>${slCell}</td>
      <td class="pos">${t.tp1 != null ? fCur(t.tp1) : '—'}</td>
      <td class="pos">${t.tp2 != null ? fCur(t.tp2) : '—'}</td>
      <td class="txt"><span class="badge ${st === 'Open' ? 'b-buy' : st === 'Partial' ? 'b-wait' : 'b-sell'}">${esc(st)}</span></td>
      <td style="white-space:nowrap"><button class="glassbtn" data-action="openAddPosition" data-args='["${t.id}"]' style="padding:3px 8px">Add</button> <button class="glassbtn" data-action="editTr" data-args='["${t.id}"]' style="padding:3px 8px">Edit</button> <button class="glassbtn" data-action="openExit" data-args='["${t.id}"]' style="padding:3px 8px">Exit</button></td>
    </tr>`;
  }).join('') || '<tr><td colspan="17" class="neo-empty">No open trades</td></tr>';
  // ===== Quiet table (P4, mockup v1.1 approved 2026-10-17) =====
  // The same `rows` the 17-column table was just drawn from, read a second way: eight fields at
  // rest, the other nine and the row's actions open with the row. The live table above stays in
  // the DOM and in Neon; the Zen skin shows this one. No figure is computed twice: every cell
  // below is the value the table cell carries, formatted by the same helpers.
  const qEl = $('trQuiet');
  if (qEl) {
    const detail = pairs => pairs.map(([k, v]) => `<span><em>${esc(k)}</em>${v}</span>`).join('');
    const qh = '<div class="qh"><div>Position</div><div>Entry</div><div>Price</div><div>P&amp;L</div><div>P&amp;L %</div><div>SL → TP1</div><div>Exp P&amp;L</div><div></div></div>';
    qEl.innerHTML = qh + (rows.map(r => {
      const t = r.t; const cur = r.cur;
      const fCur = cur === 'TRY' ? fT : cur === 'EUR' ? (v => v == null || isNaN(v) ? '—' : '€' + f(v)) : fU;
      const hzL = { short: 'S', medium: 'M', long: 'L' }[t.horizon || 'short'] || 'S';
      const isOpen = _quietOpen.has(t.id) ? ' open' : '';
      const chev = `<div class="qx" data-action="trQuietToggle" data-args='["${t.id}"]' role="button" aria-label="Details" tabindex="0">›</div>`;
      if (r.isTD) {
        const a = tdAccrued(t.td); const isDaily = t.td.kind === 'daily';
        const rate = a ? a.rate.toFixed(2) + '%' : '—';
        const term = isDaily ? 'open-ended' : (a ? a.maturityDate.slice(0, 10) : '—');
        const status = (!isDaily && a && a.matured) ? 'Matured' : t.status;
        const acts = (isDaily ? `<button class="glassbtn" data-action="changeTdRate" data-args='["${t.id}"]'>Rate</button>` + `<button class="glassbtn q-warn" data-action="closeDailyTd" data-args='["${t.id}"]'>Close</button>` : ((a && a.matured && !t.td.confirmed) ? `<button class="glassbtn q-ok" data-action="confirmMaturity" data-args='["${t.id}"]'>Mature ✓</button>` : '')) + `<button class="glassbtn" data-action="editTr" data-args='["${t.id}"]'>Edit</button>`;
        return `<div class="qr${isOpen}" data-tid="${t.id}"><div class="qp" data-action="trQuietToggle" data-args='["${t.id}"]'><b><span class="tk">${esc((t.ticker || '').slice(0, 3))}</span>${esc(t.ticker)}</b><small>${isDaily ? 'TD-D' : 'TD-M'}${t.td.bank ? ' · <span>' + esc(t.td.bank) + '</span>' : ''} · ${a ? (isDaily ? a.daysOpen + 'd' : t.td.termMonths + 'mo') : '—'} · ${rate}</small></div>
          <div class="qn">${fCur(t.td.principal)}</div><div class="qn mu">—</div><div class="qn pos">${a ? fCur(a.accruedNet) : '—'}</div><div class="qn mu">${r.pnlP != null ? fP(r.pnlP) : '—'}</div>
          <div class="qrail none">${term}</div><div class="qn mu">${a ? fCur(isDaily ? a.currentValue : a.netMaturity) : '—'}</div>${chev}
          <div class="qd">${detail([['Type', isDaily ? 'TD-D' : 'TD-M'], ['HZ', hzL], ['Cur', esc(cur)], ['Principal', fCur(t.td.principal)], [isDaily ? 'Days open' : 'Term', a ? (isDaily ? a.daysOpen + 'd' : t.td.termMonths + 'mo') : '—'], ['Rate', rate], ['Accrued', a ? fCur(a.accruedNet) : '—'], [isDaily ? 'Maturity' : 'Days to maturity', isDaily ? '∞' : (a ? a.daysToMat + 'd' : '—')], [isDaily ? 'Value now' : 'Net at maturity', a ? fCur(isDaily ? a.currentValue : a.netMaturity) : '—'], ['Status', esc(status)]])}<span class="acts">${acts}</span></div></div>`;
      }
      const st = t.status || 'Open';
      const priceCell = r.price != null
        ? `<span class="q-edit" title="Click to edit" data-action="inlineTPrice" data-args='["${t.ticker}",${r.price}]'>${fCur(r.price)}</span>`
        : `<input class="price-input" placeholder="Price..." data-action-change="manTP" data-args='["${t.ticker}"]'>`;
      const pnlCls = r.pnlLocal == null ? 'mu' : pc(r.pnlLocal);   // pos / neg / neu, the table's own rule
      const pctCls = r.pnlP == null ? 'mu' : pc(r.pnlP);
      const pnlCell = r.pnlLocal != null ? `${r.pnlLocal >= 0 ? '+' : ''}${fCur(r.pnlLocal)}<small>${r.pnlUSD >= 0 ? '+' : ''}${fU(r.pnlUSD)}</small>` : '—';
      const slTxt = fCur(r.slEff) + (r.isDefSL ? ' ·def' : '');
      // the rail: SL at the left end, TP1 at the right, entry as a tick, price as the cursor
      const dom = (t.tp1 != null && r.floor != null) ? (t.tp1 - r.floor) : 0;
      const pos = v => Math.max(0, Math.min((v - r.floor) / dom * 100, 100));
      const past = _bufPct(r) != null && _bufPct(r) < 0;   // the one predicate Proximity's .pr-past uses
      const rail = dom && r.price != null
        ? `<i class="e" style="left:${pos(t.entry).toFixed(1)}%"></i><i class="c" style="left:${pos(r.price).toFixed(1)}%"></i><em class="l">${slTxt}</em><em class="r">${fCur(t.tp1)}</em>`
        : `<em class="l">${slTxt}</em><em class="r">${t.tp1 != null ? fCur(t.tp1) : 'no TP1'}</em>`;
      return `<div class="qr${past ? ' alert' : ''}${isOpen}" data-tid="${t.id}"><div class="qp" data-action="trQuietToggle" data-args='["${t.id}"]'><b><span class="tk">${esc((t.ticker || '').slice(0, 3))}</span>${esc(t.ticker)}</b><small>${esc(t.type)} · ${esc(t.side)} · ${f(t.qty, t.type === 'Crypto' ? 6 : 2)} · ${r.lev}×</small></div>
        <div class="qn">${fCur(t.entry)}</div><div class="qn">${priceCell}</div>
        <div class="qn ${pnlCls}">${pnlCell}</div><div class="qn ${pctCls}">${r.pnlP != null ? fP(r.pnlP) : '—'}</div>
        <div class="qrail">${rail}</div>
        <div class="qn mu">${r.expLocal != null ? fCur(r.expLocal) : '—'}</div>${chev}
        <div class="qd">${detail([['Type', esc(t.type)], ['HZ', hzL], ['Side', esc(t.side)], ['Cur', esc(cur)], ['Qty', f(t.qty, t.type === 'Crypto' ? 6 : 2)], ['Lev', r.lev + '×'], ['SL', slTxt], ['TP1', t.tp1 != null ? fCur(t.tp1) : '—'], ['TP2', t.tp2 != null ? fCur(t.tp2) : '—'], ['Status', esc(st)]])}<span class="acts">${`<button class="glassbtn" data-action="openAddPosition" data-args='["${t.id}"]'>Add</button>`}${`<button class="glassbtn" data-action="editTr" data-args='["${t.id}"]'>Edit</button>`}${`<button class="glassbtn q-danger" data-action="openExit" data-args='["${t.id}"]'>Exit</button>`}</span></div></div>`;
    }).join('') || '<div class="neo-empty">No open trades</div>');
  }
  // journal (closed, from realized log)
  $('trJrnBody').innerHTML = (S.realized || []).slice(0, 30).map(r => {
    const v = realizedUSD(r);
    return `<tr>
      <td class="txt"><span style="display:flex;align-items:center;gap:8px"><span class="tk">${esc((r.ticker || '?').slice(0, 3))}</span><b>${esc(r.ticker || '')}</b></span></td>
      <td style="color:var(--t2)">${r.exit != null ? f(r.exit) : '—'}</td>
      <td class="${pc(v)}">${v >= 0 ? '+' : ''}${fU(v)}</td>
      <td class="txt"><span class="badge ${v >= 0 ? 'b-buy' : 'b-sell'}">${v >= 0 ? 'WIN' : 'LOSS'}</span></td>
    </tr>`;
  }).join('') || '<tr><td colspan="4" class="neo-empty">No closed trades</td></tr>';
  // ===== Trade Journal — expand-on-demand detail + stats (progressive disclosure) =====
  const jd = $('trJrnDetail');
  if (jd) {
    const rl = (S.realized || []);
    if (!rl.length) { jd.innerHTML = '<div class="neo-empty">No closed trades yet.</div>'; }
    else {
      // enrich each realized exit with its (closed) trade for side / horizon / hold-days
      const jrows = rl.map(r => {
        const t = tradeForRealized(r, S.trades);
        const vUSD = realizedUSD(r);
        const pnlP = (r.entry && r.qty) ? (r.pnl / (r.entry * r.qty) * 100) : null;
        const openD = t && (t.openDate || t.date), close = r.date || (t && t.closeDate);
        const days = (openD && close) ? Math.max(1, Math.round((new Date(close) - new Date(openD)) / 86400000)) : null;
        return { r, t, vUSD, pnlP, days, side: t ? t.side : '—', hz: (t && t.horizon) || '—', cur: r.currency || 'USD' };
      }).sort((a, b) => new Date(b.r.date || 0) - new Date(a.r.date || 0));
      // stats
      const jwins = jrows.filter(x => x.vUSD >= 0).length;
      const totUSD = jrows.reduce((s, x) => s + x.vUSD, 0);
      const dd = jrows.filter(x => x.days != null);
      const avgDays = dd.length ? Math.round(dd.reduce((s, x) => s + x.days, 0) / dd.length) : null;
      const hzMap = { short: 'S', medium: 'M', long: 'L' };
      const hzC = { short: 'var(--ora)', medium: 'var(--yel)', long: 'var(--pur)' };
      const stat = (l, v, cls) => `<div class="js-c"><div class="js-l">${l}</div><div class="js-v ${cls || ''}">${v}</div></div>`;
      const statsHtml = `<div class="jrn-stats">
        ${stat('Closed', jrows.length)}
        ${stat('Realized', (totUSD >= 0 ? '+' : '') + fU(totUSD), pc(totUSD))}
        ${stat('Win rate', Math.round(jwins / jrows.length * 100) + '%', 'pos')}
        ${stat('Avg hold', avgDays != null ? avgDays + 'd' : '—')}
      </div>`;
      const body = jrows.map(x => {
        const r = x.r; const fcx = v => v == null ? '—' : fByCur(x.cur, v);
        const hzL = hzMap[x.hz] || (x.hz === '—' ? '—' : 'S'); const hzCol = hzC[x.hz] || 'var(--t3)';
        return `<tr>
          <td class="txt"><b>${esc(r.ticker || '')}</b></td>
          <td><span style="font-size:10px;font-weight:600;color:${hzCol}">${hzL}</span></td>
          <td class="txt"><span class="badge ${x.side === 'Short' ? 'b-sell' : 'b-buy'}">${esc(x.side)}</span></td>
          <td>${esc(x.cur)}</td>
          <td>${fcx(r.entry)}</td>
          <td>${fcx(r.exit)}</td>
          <td class="${pc(r.pnl)}">${r.pnl >= 0 ? '+' : ''}${fcx(r.pnl)}<span style="color:var(--t3);font-size:10px;display:block">${x.vUSD >= 0 ? '+' : ''}${fU(x.vUSD)}</span></td>
          <td class="${pc(x.pnlP)}">${x.pnlP != null ? fP(x.pnlP) : '—'}</td>
          <td style="color:var(--t2)">${x.days != null ? x.days + 'd' : '—'}</td>
          <td style="color:var(--t2);font-size:11px">${esc(r.reason || '—')}</td>
          <td class="txt"><span class="badge ${x.vUSD >= 0 ? 'b-buy' : 'b-sell'}">${x.vUSD >= 0 ? 'WIN' : 'LOSS'}</span></td>
        </tr>`;
      }).join('');
      jd.innerHTML = statsHtml + `<div style="max-height:340px;overflow:auto;margin-top:10px"><table class="tbl"><thead><tr><th>Ticker</th><th>HZ</th><th>Side</th><th>Cur</th><th>Entry</th><th>Exit</th><th>P&amp;L</th><th>P&amp;L%</th><th>Days</th><th>Reason</th><th>Result</th></tr></thead><tbody>${body}</tbody></table></div>`;
    }
  }
  _trFolds({ totRisk, totReward, pctNW, rm: rm.map(r => [r.tk, r.Rtp]) });
}

// ===== Trade Journal Renderer =====

function renderTradeJournal() {
  // Journal subpage owns the AI-journal containers (tj-exp-<id>) now. The hidden
  // legacy table would create duplicate ids and hijack getElementById — skip it.
  if (document.getElementById('jrnRows')) return;
  const closedTrades = S.trades.filter(t => t.status === 'Closed');
  const tjEl = document.getElementById('tradeJournal');
  if (!tjEl) return;
  if (!closedTrades.length && !S.realized.length) {
    tjEl.style.display = 'none';
    return;
  }
  tjEl.style.display = 'block';

  // Build journal entries from closed trades + realized records
  const entries = [];
  closedTrades.forEach(t => {
    const cur = t.currency || 'USD';
    const fc = v => fByCur(cur, v);
    // ⚠ WAS `r.tradeId === t.id || (!r.tradeId && r.ticker === t.ticker)`, with no status and no
    // date. Two closed TRYVD2 deposits and two unlinked TRYVD2 rows would have given EACH entry
    // BOTH deposits' interest — the sum below is what would have been wrong.
    const relatedR = realizedForTrade(t, S.realized, S.trades);
    const avgExitQty = relatedR.reduce((s, r) => s + r.qty, 0);
    const avgExit = avgExitQty > 0
      ? relatedR.reduce((s, r) => s + r.exit * r.qty, 0) / avgExitQty
      : 0;
    const totalPnl = relatedR.reduce((s, r) => s + r.pnl, 0);
    const totalPnlUSD = relatedR.reduce(
      (s, r) => s + (realizedUSD(r)),
      0
    );
    const pnlPct =
      t.entry > 0
        ? (totalPnl / (t.entry * (relatedR.reduce((s, r) => s + r.qty, 0) || 1))) * 100
        : 0;
    const openDate = t.openDate || t.date || relatedR[0]?.date;
    const closeDate = t.closeDate || relatedR[relatedR.length - 1]?.date;
    const days =
      openDate && closeDate
        ? Math.max(1, Math.round((new Date(closeDate) - new Date(openDate)) / 86400000))
        : null;
    const reason = relatedR.map(r => r.reason).filter(Boolean).join(', ') || '—';
    entries.push({
      id: t.id,
      ticker: t.ticker,
      side: t.side,
      cur,
      entry: t.entry,
      exit: avgExit,
      pnl: totalPnl,
      pnlUSD: totalPnlUSD,
      pnlPct,
      days,
      reason,
      win: totalPnlUSD >= 0,
      fc,
      date: closeDate,
      horizon: t.horizon || 'short',
      journal: t.journal || []
    });
  });

  // If no closed trades but have realized records (from partial exits), show those
  if (!entries.length) {
    S.realized.forEach(r => {
      const t = S.trades.find(x => x.ticker === r.ticker);
      const cur = r.currency || 'USD';
      const fc = v => fByCur(cur, v);
      const pnlUSD = realizedUSD(r);
      const pnlPct = r.entry > 0 ? (((r.exit - r.entry) / r.entry) * 100) : 0;
      entries.push({
        ticker: r.ticker,
        side: t?.side || 'Long',
        cur,
        entry: r.entry,
        exit: r.exit,
        pnl: r.pnl,
        pnlUSD,
        pnlPct,
        days: null,
        reason: r.reason || '—',
        win: pnlUSD >= 0,
        fc,
        date: r.date,
        horizon: t?.horizon || 'short'
      });
    });
  }

  if (!entries.length) {
    tjEl.style.display = 'none';
    return;
  }

  // Stats
  const wins = entries.filter(e => e.win).length;
  const losses = entries.length - wins;
  const winRate = entries.length > 0 ? (wins / entries.length) * 100 : 0;
  const totalPnlUSD = entries.reduce((s, e) => s + e.pnlUSD, 0);
  const avgReturn = entries.length > 0 ? entries.reduce((s, e) => s + e.pnlPct, 0) / entries.length : 0;
  const daysArr = entries.filter(e => e.days).map(e => e.days);
  const avgDays = daysArr.length > 0 ? Math.round(daysArr.reduce((s, d) => s + d, 0) / daysArr.length) : null;
  const avgWin = wins > 0 ? entries.filter(e => e.win).reduce((s, e) => s + e.pnlUSD, 0) / wins : 0;
  const avgLoss =
    losses > 0 ? entries.filter(e => !e.win).reduce((s, e) => s + e.pnlUSD, 0) / losses : 0;
  const pf = avgLoss < 0 ? Math.abs(avgWin / avgLoss) : avgWin > 0 ? Infinity : 0;

  document.getElementById('tjStats').innerHTML = `
    <div class="neo-cell"><div class="l">Trades</div><div class="v">${entries.length}</div><div class="s">${wins}W / ${losses}L</div></div>
    <div class="neo-cell"><div class="l">Win Rate</div><div class="v ${winRate >= 50 ? 'pos' : 'neg'}">${f(
    winRate,
    1
  )}%</div></div>
    <div class="neo-cell"><div class="l">Total P&L</div><div class="v ${pc(totalPnlUSD)}">${fU(
    totalPnlUSD
  )}</div></div>
    <div class="neo-cell"><div class="l">Avg Return</div><div class="v ${pc(avgReturn)}">${fP(
    avgReturn
  )}</div></div>
    <div class="neo-cell"><div class="l">Avg Hold</div><div class="v">${avgDays ? avgDays + 'd' : '—'}</div></div>
    <div class="neo-cell"><div class="l">Profit Factor</div><div class="v ${pf >= 1 ? 'pos' : 'neg'}">${
    pf === Infinity ? '∞' : f(pf, 2)
  }</div></div>`;

  // Horizon breakdown
  const hzStats = ['short', 'medium', 'long']
    .map(hz => {
      const hE = entries.filter(e => e.horizon === hz);
      if (!hE.length) return null;
      const hW = hE.filter(e => e.win).length;
      const hWR = hE.length ? (hW / hE.length) * 100 : 0;
      const hPnl = hE.reduce((s, e) => s + e.pnlUSD, 0);
      const hAvg = hE.reduce((s, e) => s + e.pnlPct, 0) / hE.length;
      const hDays = hE.filter(e => e.days).map(e => e.days);
      const hAvgD =
        hDays.length > 0 ? Math.round(hDays.reduce((s, d) => s + d, 0) / hDays.length) : null;
      const label = { short: 'Short', medium: 'Medium', long: 'Long' }[hz];
      const col = { short: 'var(--ora)', medium: 'var(--yel)', long: 'var(--pur)' }[hz];
      return `<div class="gls-soft" style="flex:1;min-width:160px;border:1px solid var(--bd);border-radius:8px;padding:10px 12px;border-left:3px solid ${col}">
      <div style="font-size:11px;font-weight:600;color:${col};margin-bottom:6px">${label} (${hE.length})</div>
      <div style="font-size:10px;color:var(--t3)">Win Rate: <span class="${hWR >= 50 ? 'pos' : 'neg'}">${f(
        hWR,
        1
      )}%</span></div>
      <div style="font-size:10px;color:var(--t3)">P&L: <span class="${pc(hPnl)}">${fU(hPnl)}</span></div>
      <div style="font-size:10px;color:var(--t3)">Avg Ret: <span class="${pc(hAvg)}">${fP(
        hAvg
      )}</span></div>
      <div style="font-size:10px;color:var(--t3)">Avg Hold: ${hAvgD ? hAvgD + 'd' : '—'}</div>
    </div>`;
    })
    .filter(Boolean);
  const hzEl = document.getElementById('tjHzStats');
  if (hzEl) {
    hzEl.innerHTML = hzStats.length
      ? hzStats.join('')
      : '<div class="neo-empty">No horizon data yet</div>';
  }

  const _jBadge = typeof window.getJournalBadge === 'function' ? window.getJournalBadge : () => '';
  const _jExpRow = typeof window.getJournalExpandRow === 'function' ? window.getJournalExpandRow : () => '';

  document.getElementById('tjBody').innerHTML = entries
    .sort((a, b) => ((b.date || '') > (a.date || '') ? 1 : -1))
    .map(e => {
      const hzB = e.horizon || 'short';
      const hzL = { short: 'S', medium: 'M', long: 'L' }[hzB] || 'S';
      const hzC = {
        short: 'var(--ora)',
        medium: 'var(--yel)',
        long: 'var(--pur)'
      }[hzB] || 'var(--t3)';
      const trade = S.trades.find(t => t.id === e.id);
      const badge = trade ? _jBadge(trade) : '';
      const expandRow = e.id ? _jExpRow(e.id, 11) : '';
      return `<tr style="cursor:pointer" data-action="toggleJournalExpand" data-args='["${esc(e.id || '')}"]'>
      <td><strong>${esc(e.ticker)}</strong>${badge}</td><td><span style="font-size:10px;font-weight:600;color:${hzC}">${hzL}</span></td><td>${esc(
        e.side
      )}</td><td>${esc(e.cur)}</td>
      <td class="tr">${e.fc(e.entry)}</td><td class="tr">${e.fc(e.exit)}</td>
      <td class="tr ${pc(e.pnlUSD)}">${fU(e.pnlUSD)}</td><td class="tr ${pc(e.pnlPct)}">${fP(
        e.pnlPct
      )}</td>
      <td class="tr">${e.days || '—'}</td><td style="color:var(--t3)">${esc(e.reason)}</td>
      <td><span class="badge ${e.win ? 'b-open' : 'b-stopped'}">${e.win ? 'WIN' : 'LOSS'}</span></td></tr>${expandRow}`;
    })
    .join('');

  // Re-render any expanded journal areas
  if (typeof window.refreshExpandedJournals === 'function') window.refreshExpandedJournals();
}

// ===== Trade CRUD =====

function openAddT() {
  editTrId = null;
  // ⚠ `tdNonEarn` WAS MISSING HERE WHILE `editTr` CLEARED IT, so a new deposit inherited the
  // last edited one's non-earning portion. On a TRY 2,000,000 deposit an inherited 95,397.88
  // silently removes that much from the accrual base and nothing on the form says why the
  // interest looks low -- the same shape as the `_IMPORT_KEYS` omissions, which have cost live
  // data twice. The two doors are pinned together by
  // `two-doors-open-the-same-form.test.js`: every `td*` field cleared in one is cleared in the
  // other, so the next field cannot drift either.
  ['tTk', 'tEn', 'tQt', 'tSl', 'tT1', 'tT2', 'tNo', 'tdPrincipal', 'tdStart', 'tdTerm', 'tdRate2', 'tdMatVal', 'tdBank', 'tdNonEarn'].forEach(i => {
    const el = document.getElementById(i);
    if (el) el.value = '';
  });
  document.getElementById('tRate').value = '';
  document.getElementById('tDur').value = '';
  document.getElementById('tExpPnl').value = '';
  document.getElementById('tLv').value = '1';
  document.getElementById('tCur').value = 'USD';
  document.getElementById('tTp').value = 'Crypto';
  document.getElementById('tSd').value = 'Long';
  document.getElementById('tHz').value = 'short';
  // Defaults to today, which is right most of the time and wrong silently the rest — so it
  // is a visible field with today in it, not an invisible assumption. (audit 2026-08-03)
  { const el = document.getElementById('tOpenDate'); if (el) el.value = new Date().toISOString().slice(0, 10); }
  const wht = document.getElementById('tdWht');
  if (wht) {
    wht.value = '10';
    delete wht.dataset.touched;
  }
  const md = document.getElementById('tdMode');
  if (md) md.value = 'rate';
  const kk = document.getElementById('tdKind');
  if (kk) kk.value = 'monthly';
  thxFill(null);
  toggleTdTradeFields();
  onTdKindChange();
  document.getElementById('moTTitle').textContent = 'New Trade';
  document.getElementById('moTBtn').textContent = 'Open Trade';
  document.getElementById('moT').classList.add('active');
}

function editTr(id) {
  const t = S.trades.find(x => x.id === id);
  if (!t) return;
  editTrId = id;
  document.getElementById('tTk').value = t.ticker;
  document.getElementById('tTp').value = t.type;
  document.getElementById('tSd').value = t.side;
  document.getElementById('tCur').value = t.currency || 'USD';
  document.getElementById('tEn').value = t.entry;
  document.getElementById('tQt').value = t.qty;
  document.getElementById('tLv').value = t.leverage;
  document.getElementById('tSl').value = t.sl || '';
  document.getElementById('tT1').value = t.tp1 || '';
  document.getElementById('tT1p').value = t.tp1Pct || 50;
  document.getElementById('tT2').value = t.tp2 || '';
  document.getElementById('tT2p').value = t.tp2Pct || 100;
  document.getElementById('tNo').value = t.note || '';
  { const el = document.getElementById('tOpenDate'); if (el) el.value = String(t.openDate || '').slice(0, 10); }
  document.getElementById('tHz').value = t.horizon || 'short';
  document.getElementById('tRate').value = t.annualRate || '';
  document.getElementById('tDur').value = t.duration || '';
  // TD prefill
  // ⚠ A FIELD MISSING FROM BOTH OF THESE LISTS IS ERASED BY EDITING THE ROW. `tdNonEarn` is in
  // both deliberately: cleared here with the rest, repopulated below. Leaving it out of the
  // second list would have had one Edit-and-Save silently put the accrual back on the whole
  // principal -- the same shape as an `_IMPORT_KEYS` omission, which has cost live data twice.
  ['tdPrincipal', 'tdStart', 'tdTerm', 'tdRate2', 'tdMatVal', 'tdBank', 'tdNonEarn'].forEach(i => {
    const el = document.getElementById(i);
    if (el) el.value = '';
  });
  const wht2 = document.getElementById('tdWht');
  if (wht2) wht2.value = '10';
  if (t.type === 'TimeDeposit' && t.td) {
    document.getElementById('tdKind').value = t.td.kind || 'monthly';
    document.getElementById('tdPrincipal').value = t.td.principal || '';
    document.getElementById('tdStart').value = t.td.startDate || '';
    document.getElementById('tdTerm').value = t.td.termMonths || '';
    document.getElementById('tdMode').value = t.td.mode || 'rate';
    document.getElementById('tdRate2').value = t.td.annualRate || '';
    document.getElementById('tdMatVal').value = t.td.maturityValue || '';
    if (wht2) {
      wht2.value = t.td.withholdingPct != null ? t.td.withholdingPct : 10;
      wht2.dataset.touched = '1';
    }
    document.getElementById('tdBank').value = t.td.bank || '';
    const _ne2 = document.getElementById('tdNonEarn');
    if (_ne2) _ne2.value = t.td.nonEarning ? t.td.nonEarning : '';
  }
  thxFill(t.thesis);
  toggleTdTradeFields();
  onTdKindChange();
  calcExpPnl();
  document.getElementById('moTTitle').textContent = 'Edit Trade — ' + t.ticker;
  document.getElementById('moTBtn').textContent = 'Save Changes';
  document.getElementById('moT').classList.add('active');
}


// ═══ THESIS (part 1 of the Trades extra context, 2026-08-16) ═══
// The four texts plus the optional watch, read from and written to the ONE modal both
// "New Trade" and "Edit Trade" use. There is no second surface: the Journal subpage is built
// from `S.realized` and therefore holds closed trades only, so an OPEN trade has nowhere else
// its thesis could live. (measured 2026-08-16)
const _THX = { text: 'tThText', trigger: 'tThTrigger', expected: 'tThExpected', invalidation: 'tThInval' };

/** Read the form into the shape `core/thesis.js` expects. */
function thxRead() {
  const g = id => (document.getElementById(id) || {}).value || '';
  const form = {};
  Object.keys(_THX).forEach(k => { form[k] = g(_THX[k]); });
  const metric = g('tThWatchM');
  form.watch = metric ? { metric, dir: g('tThWatchD'), value: parseFloat(g('tThWatchV')) } : null;
  return form;
}

/** Put a stored thesis back on the form, or clear it. */
function thxFill(th) {
  const set = (id, v) => { const el = document.getElementById(id); if (el) el.value = v == null ? '' : v; };
  Object.keys(_THX).forEach(k => set(_THX[k], th && th[k]));
  const w = validWatch(th && th.watch);
  set('tThWatchM', w ? w.metric : '');
  set('tThWatchD', w ? w.dir : 'below');
  set('tThWatchV', w ? w.value : '');
  const hint = document.getElementById('thxHint');
  if (hint) {
    hint.textContent = th && th.recordedAt
      ? 'recorded ' + String(th.recordedAt).slice(0, 10) + (th.editedAt ? ' · edited ' + String(th.editedAt).slice(0, 10) : '')
      : 'why this trade exists — blank records "not stated"';
  }
  // Open the section when there is something to see, so an existing thesis is never hidden
  // behind a collapsed header the author has to know to click.
  const sec = document.getElementById('thxSec');
  if (sec) sec.classList.toggle('open', !!(th && (th.text || th.invalidation || w)));
  thxPreview();
}

/** The condition in words, live, so the optional row explains itself as it is filled. */
export function thxPreview() {
  const box = document.getElementById('thxPrev');
  if (!box) return;
  const sentence = watchSentence({ watch: thxRead().watch });
  box.classList.toggle('off', !sentence);
  box.innerHTML = sentence
    ? 'Checked against the exit: <b>' + esc(sentence) + '</b>. Hit, and the journal reads INVALIDATION HIT.'
    : 'No condition set — the invalidation stays a written reason you judge yourself at exit.';
}

/** Header click. */
export function thxToggle() {
  const sec = document.getElementById('thxSec');
  if (sec) sec.classList.toggle('open');
}

/**
 * Merge a form-built time deposit over the existing one, keeping what the form never asked for.
 *
 * Exported because a rule that lives inside one caller is written for nobody else (rule #24) —
 * and because a test that re-implements the merge cannot fail when the merge changes, which is
 * exactly what happened to the first draft of this fix's tests.
 *
 * @param {Object|null} prev  the deposit as stored, or null/undefined when creating
 * @param {Object} form       the object literal `saveT` builds from the inputs
 */
/**
 * Why the Edit form must refuse to change a daily deposit's principal once it has a rate
 * history. `tdComputeGross` compounds from `rateSegments[i].startBalance`; the form owns
 * `principal` and nothing else. Lower the principal and the withdrawn money is not removed from
 * the accrual base, it is reclassified as interest: PHANTOM = withdrawal x (1 - withholding).
 * Measured 2026-08-21 on both live deposits: a TRY 200,000 "withdrawal" invented TRY 165,000 of
 * net worth. Raise it and the same arithmetic hides interest instead. The bank supports partial
 * withdrawal; the app expresses it as close (interest realised to cash) + open for the remainder.
 * Returns the refusal text, or null when the edit is coherent.
 */
export function tdPrincipalEditRefusal(prevTd, newPrincipal) {
  if (!prevTd || (prevTd.kind || 'monthly') !== 'daily') return null;
  if (!Array.isArray(prevTd.rateSegments) || prevTd.rateSegments.length === 0) return null;
  const was = Number(prevTd.principal) || 0, now_ = Number(newPrincipal) || 0;
  if (Math.abs(now_ - was) < 0.005) return null;
  return now_ < was
    ? 'A daily deposit with a rate history cannot have its principal lowered here: the withdrawn amount would be counted as interest. Close the deposit (interest is realised to cash) and open a new one for the remainder.'
    : 'A daily deposit with a rate history cannot have its principal raised here: the top-up would hide interest. Close the deposit and open a new one for the new amount.';
}

export function mergeTdOnEdit(prev, form) {
  return {
    ...(prev || {}),
    ...form,
    // `confirmed` IS in the form literal, hardcoded false, so spreading `form` last would
    // un-confirm a matured deposit on any edit. Confirmation is set by `confirmMaturity` and
    // the form never asks about it.
    confirmed: prev ? !!prev.confirmed : false,
    confirmedAt: prev ? prev.confirmedAt : undefined,
  };
}

function saveT() {
  // WHEN the position was opened is a fact the form never asked for. `openDate: now()`
  // stamped today on every trade, so a position entered after the fact could not be dated —
  // 200 ASELS opened 2026-05-22 and recorded on 2026-08-03 landed in August, priced its
  // currency effect at a month that has no rate yet, and reported a holding period of zero
  // days. Substituting "now" for something unknown is the same failure as substituting spot
  // for a missing rate. Ask, default to today, and record what was answered.
  // (audit 2026-08-03)
  const _thxForm = thxRead();
  const _od = (document.getElementById('tOpenDate') || {}).value;
  const _openDateISO = /^\d{4}-\d{2}-\d{2}$/.test(_od || '') ? _od + 'T12:00:00' : now();
  const tType = document.getElementById('tTp').value;
  // TimeDeposit-specific path
  if (tType === 'TimeDeposit') {
    const tk = document.getElementById('tTk').value.trim().toUpperCase();
    if (!tk) return toast('Ticker required', 'a-r');
    const td = {
      kind: document.getElementById('tdKind').value,
      principal: parseFloat(document.getElementById('tdPrincipal').value) || 0,
      startDate: document.getElementById('tdStart').value || null,
      termMonths: parseInt(document.getElementById('tdTerm').value) || 0,
      mode: document.getElementById('tdMode').value,
      annualRate: parseFloat(document.getElementById('tdRate2').value) || null,
      maturityValue: parseFloat(document.getElementById('tdMatVal').value) || null,
      withholdingPct: parseFloat(document.getElementById('tdWht').value) || 0,
      // Declared, never inferred: the part of the balance that earns nothing. Akbank keeps 10%
      // of a breakable TRY deposit this way and the model used to accrue on all of it, which
      // overstated fourteen days of interest by TRY 2,678.71, 11.3%. See math.tdEarningBase.
      nonEarning: parseFloat(document.getElementById('tdNonEarn').value) || 0,
      bank: document.getElementById('tdBank').value.trim(),
      confirmed: false
    };
    if (!td.principal || !td.startDate) return toast('Need principal and start date', 'a-r');
    if (td.kind === 'monthly') {
      if (!td.termMonths) return toast('Term (months) required', 'a-r');
      if (td.mode === 'rate' && !td.annualRate) return toast('Annual rate required', 'a-r');
      if (td.mode === 'maturity' && !td.maturityValue)
        return toast('Maturity value required', 'a-r');
    } else {
      if (!td.annualRate) return toast('Annual rate required', 'a-r');
    }
    const cur = document.getElementById('tCur').value;
    const d = {
      ticker: tk,
      type: 'TimeDeposit',
      side: 'Long',
      currency: cur,
      entry: td.principal,
      qty: 1,
      leverage: 1,
      sl: null,
      tp1: null,
      tp2: null,
      note: document.getElementById('tNo').value.trim(),
      horizon: document.getElementById('tHz').value,
      td
    };
    if (editTrId) {
      const t = S.trades.find(x => x.id === editTrId);
      if (t) {
        const _refuse = tdPrincipalEditRefusal(t.td, td.principal);
        if (_refuse) return toast(_refuse, 'a-r', { duration: 9000 });
        // Fix #2: Adjust cash for principal delta on TD edit
        const oldPrincipal = t.td?.principal || 0;
        const oldCur = t.currency || 'TRY';
        const newPrincipal = td.principal;
        const delta = newPrincipal - oldPrincipal;
        if (delta !== 0 || oldCur !== cur) {
          S.cash[oldCur] = (S.cash[oldCur] || 0) + oldPrincipal; // reverse old
          S.cash[cur] = (S.cash[cur] || 0) - newPrincipal; // apply new
          S.cashTxs.unshift({
            id: uid(),
            date: now(),
            type: 'td_topup',
            amount: delta !== 0 ? -delta : 0,
            currency: cur,
            note: `TD ${tk} edited: ${fByCur(oldCur, oldPrincipal)} → ${fByCur(cur, newPrincipal)}`
          });
          console.log(
            'cash',
            tk,
            `TD reconciled: ${oldCur} +${f(oldPrincipal)}, ${cur} -${f(newPrincipal)}`
          );
        }
        // ⚠ THE FORM DOES NOT OWN EVERY FIELD ON `td`, AND A WHOLESALE REPLACE DOES NOT KNOW
        // THAT.
        //
        // `d.td` is the object literal built above from the form inputs: kind, principal,
        // startDate, termMonths, mode, annualRate, maturityValue, withholdingPct, bank,
        // confirmed. `Object.assign(t, d)` then replaced `t.td` with it — so every field the
        // FORM does not ask about was deleted by an edit:
        //
        //   `rateSegments`  the rate history. All three of his deposits carry two segments
        //                   (43→42, 42→40, 42→40). Editing one — even to correct the bank
        //                   name — dropped it, and `_dailyTdSegments` then falls back to
        //                   `[{ rate: annualRate }]`, repricing the WHOLE life at the current
        //                   rate. On the 2026-04-30 deposit that is ₺26,177 → ₺24,940 of gross
        //                   interest, and since TAX-002 wired the tax summary to the same
        //                   function, the 2026 tax base would drop by the same amount without
        //                   anything being said.
        //   `confirmed` /   maturity confirmation, which `confirmMaturity` sets and this
        //   `confirmedAt`   literal hardcodes back to `false`. Editing a confirmed deposit
        //                   un-confirmed it.
        //
        // Third instance of this shape today: `_IMPORT_KEYS` strips on import, the cloud merge
        // replaced `settings` wholesale (SEC-001), and this. **A wholesale replace assembled
        // from a form drops every field the form has never heard of.** Merge instead, and let
        // the form win only on what it actually asked.
        d.td = mergeTdOnEdit(t.td, td);
        // Same merge, same reason (see the note on the non-TD edit below).
        d.thesis = mergeThesisOnEdit(t.thesis, _thxForm, now());
        Object.assign(t, d);
        stampItem(t);
        console.log('trade', tk, 'TD edited');
      }
    } else {
      // A blank form leaves NO field at all — the journal reads that as NOT RECORDED. Writing
      // an empty object would be a default dressed as a statement.
      const _th1 = buildThesis(_thxForm, now(), null);
      const t = { id: uid(), ...d, ...(_th1 ? { thesis: _th1 } : {}), status: 'Open', openDate: _openDateISO, exits: [] };
      S.trades.push(t);
      S.cash[cur] = (S.cash[cur] || 0) - td.principal;
      S.cashTxs.unshift({
        id: uid(),
        date: _openDateISO,
        type: 'td_open',
        amount: -td.principal,
        currency: cur,
        note: `Open TimeDeposit ${tk} (${td.bank || ''} ${td.termMonths}mo)`
      });
      console.log('trade', tk, `TimeDeposit ${f(td.principal)} ${cur} ${td.termMonths}mo`);
    }
    editTrId = null;
    save();
    markDirty('cash', 'dash');
    clMo('moT');
    renderT();
    toast(tk + ' saved', 'a-b');
    return;
  }
  const tk = document.getElementById('tTk').value.trim().toUpperCase(),
    en = parseFloat(document.getElementById('tEn').value),
    qt = parseFloat(document.getElementById('tQt').value);
  if (!tk || !en || !qt) return toast('Required: ticker, entry price, quantity', 'a-r');
  // F-020: Input bounds validation
  if (en <= 0) return toast('Entry price must be positive', 'a-r');
  if (qt <= 0) return toast('Quantity must be positive', 'a-r');
  const lvVal = parseFloat(document.getElementById('tLv').value) || 1;
  if (lvVal < 1 || lvVal > 100) return toast('Leverage must be 1-100x', 'a-r');
  const _curVal = document.getElementById('tCur').value;
  // VALID_CURRENCIES would be defined globally or imported
  const d = {
    ticker: tk,
    type: tType,
    side: document.getElementById('tSd').value,
    currency: document.getElementById('tCur').value,
    entry: en,
    qty: qt,
    leverage: parseFloat(document.getElementById('tLv').value) || 1,
    sl: parseFloat(document.getElementById('tSl').value) || null,
    tp1: parseFloat(document.getElementById('tT1').value) || null,
    tp1Pct: parseFloat(document.getElementById('tT1p').value) || 50,
    tp2: parseFloat(document.getElementById('tT2').value) || null,
    tp2Pct: parseFloat(document.getElementById('tT2p').value) || 100,
    note: document.getElementById('tNo').value.trim(),
    horizon: document.getElementById('tHz').value,
    annualRate: parseFloat(document.getElementById('tRate').value) || null,
    duration: parseInt(document.getElementById('tDur').value) || null
  };
  // T-2: TP/SL ordering sanity — reject invalid targets before they enter the books (skips TimeDeposits, which have null TP/SL)
  if (d.tp1 != null && d.tp2 != null && d.tp1 === d.tp2) return toast('TP1 and TP2 cannot be equal', 'a-r');
  if (d.side === 'Long') {
    if (d.tp1 != null && d.tp1 <= d.entry) return toast('TP1 must be above entry for a Long', 'a-r');
    if (d.tp2 != null && d.tp2 <= d.entry) return toast('TP2 must be above entry for a Long', 'a-r');
    if (d.sl != null && d.sl >= d.entry) return toast('Stop-loss must be below entry for a Long', 'a-r');
  } else if (d.side === 'Short') {
    if (d.tp1 != null && d.tp1 >= d.entry) return toast('TP1 must be below entry for a Short', 'a-r');
    if (d.tp2 != null && d.tp2 >= d.entry) return toast('TP2 must be below entry for a Short', 'a-r');
    if (d.sl != null && d.sl <= d.entry) return toast('Stop-loss must be above entry for a Short', 'a-r');
  }
  if (editTrId) {
    const t = S.trades.find(x => x.id === editTrId);
    if (t) {
      // CE-07 Fix: Block entry/qty/currency edits on partially exited trades
      const hasPartialExits = t.exits && t.exits.length > 0;
      if (
        hasPartialExits &&
        (d.entry !== t.entry || d.qty !== t.qty || d.currency !== t.currency)
      ) {
        return toast(
          'Cannot change entry/qty/currency on partially exited trade. Close it and open a new one.',
          'a-r'
        );
      }
      // Reverse old cash deduction & apply new one if entry/qty/currency changed
      // Use _originalQty if available (tracks pre-exit quantity)
      const origQty = t._originalQty || t.qty;
      const oldCost = t.entry * origQty;
      const oldCur = t.currency || 'USD';
      const newCost = d.entry * d.qty;
      const newCur = d.currency;
      if (oldCost !== newCost || oldCur !== newCur) {
        S.cash[oldCur] = (S.cash[oldCur] || 0) + oldCost; // reverse old
        S.cash[newCur] = (S.cash[newCur] || 0) - newCost; // apply new
        // Remove old cash tx (tombstone it) and add corrected one — otherwise a merge resurrects the old
        // trade_open tx and the trade's cash cost is deducted twice.
        (S.cashTxs || []).forEach(x => {
          if (x.type === 'trade_open' && (x.tradeId === t.id || (x.note && x.note.startsWith('Open ' + t.ticker + ' ')))) tombstoneId(x.id);
        });
        S.cashTxs = S.cashTxs.filter(
          x => !(x.type === 'trade_open' && (x.tradeId === t.id || (x.note && x.note.startsWith('Open ' + t.ticker + ' '))))
        );
        S.cashTxs.unshift({
          id: uid(),
          date: _openDateISO,
          type: 'trade_open',
          tradeId: t.id,
          amount: -newCost,
          currency: newCur,
          note: 'Open ' + tk + ' ' + d.side + ' (edited)'
        });
        console.log(
          'cash',
          tk,
          `Reconciled: reversed ${oldCur} ${f(oldCost)}, applied ${newCur} ${f(newCost)}`
        );
      }
      // The open date is editable too — a trade recorded weeks after the fact has to be
      // correctable, or the only way to fix it is to delete and re-enter it.
      //
      // And its CASH ROW has to follow. The rewrite above only fires when cost or currency
      // changed, so correcting the date alone left the trade on 2026-05-22 and the ₺79,100
      // that paid for it on 2026-08-03 — a ledger disagreeing with the position about when
      // the money moved, which is the thing dating it was supposed to fix. Re-date in place:
      // the amount and the balance are already right, only the day was wrong.
      // (audit 2026-08-03)
      // ⚠ THE FORM DOES NOT OWN EVERY FIELD ON `thesis` EITHER — same shape as `td` above.
      // `Object.assign(t, d)` replaces wholesale, and the form has never heard of `recordedAt`,
      // so a plain assign would restamp an eight-week-old thesis as written today. Merge, and
      // let the form win only on what it asked. Returns null only on a deliberate erase.
      d.thesis = mergeThesisOnEdit(t.thesis, _thxForm, now());
      const _prevOpen = t.openDate;
      Object.assign(t, d);
      if (_od) t.openDate = _openDateISO;
      if (_od && String(_prevOpen || '').slice(0, 10) !== String(t.openDate).slice(0, 10)) {
        (S.cashTxs || []).forEach(x => {
          const mine = x.tradeId === t.id
            || (x.note && x.note.startsWith('Open ' + t.ticker + ' '))
            || (x.note && x.note.startsWith('Open TimeDeposit ' + t.ticker));
          if (mine && (x.type === 'trade_open' || x.type === 'td_open')) {
            x.date = t.openDate;
            stampItem(x);
          }
        });
        (t.lots || []).forEach(l => { if (l && !l._userDated) l.date = String(t.openDate).slice(0, 10); });
      }
      stampItem(t);
      console.log('trade', tk, 'Edited');
    }
  } else {
    const _th2 = buildThesis(_thxForm, now(), null);
    const t = {
      id: uid(),
      ...d,
      ...(_th2 ? { thesis: _th2 } : {}),
      _originalQty: d.qty,
      status: 'Open',
      openDate: _openDateISO,
      exits: []
    };
    S.trades.push(t);
    console.log('trade', tk, `${d.side} ${f(qt)} @ ${en} ${d.leverage}x`);
    const cost = d.entry * d.qty;
    const cur = d.currency;
    S.cash[cur] = (S.cash[cur] || 0) - cost;
    S.cashTxs.unshift({
      id: uid(),
      date: _openDateISO,
      type: 'trade_open',
      tradeId: t.id,
      amount: -cost,
      currency: cur,
      note: 'Open ' + tk + ' ' + d.side
    });
  }
  editTrId = null;
  save();
  markDirty('cash', 'dash');
  clMo('moT');
  renderT();
  toast(tk + ' saved', 'a-b');
}

// ===== Trade Exit =====

let editTId = null; // Note: this is used in execExit

function openExit(id) {
  editTId = id;
  const t = S.trades.find(x => x.id === id);
  if (!t) return;
  document.getElementById('eP').value = S.prices[t.ticker]
    ? S.prices[t.ticker].price
    : '';
  document.getElementById('eQ').value = t.qty;
  // Reset exit reason to default (prevent stale carry-over from previous trade)
  const eR = document.getElementById('eR');
  if (eR) eR.value = 'Manual';
  document.getElementById('moTE').classList.add('active');
}

function execExit() {
  const t = S.trades.find(x => x.id === editTId);
  if (!t) return;
  const ep = parseFloat(document.getElementById('eP').value),
    eq = parseFloat(document.getElementById('eQ').value),
    er = document.getElementById('eR').value;
  if (!ep || ep <= 0) return toast('Exit price must be positive', 'a-r');
  if (!eq || eq <= 0) return toast('Quantity must be positive', 'a-r');
  if (eq > t.qty) return toast('Cannot exit more than open qty (' + f(t.qty) + ')', 'a-r');
  const cur = t.currency || 'USD';
  const fc = v => fByCur(cur, v);
  // Margin model: entry deducts entry*qty as margin. P&L is leveraged. Proceeds = margin + leveraged P&L.
  // Exit commission. Trades carry no broker today, so the model resolves to unknown and the
  // fee is null — proceeds stay gross and the record says so, rather than a guessed 0.195%
  // being written into realized P&L. unknownFeeTrades surfaces the gap. (audit 2026-08-03)
  const _sale = priceSale(t, eq, ep);
  const pnl = (t.side === 'Long' ? ep - t.entry : t.entry - ep) * eq * t.leverage
    - (_sale.feeLocal || 0) * (t.leverage || 1);
  // A 0 written here is PERMANENT: realizedUSD() reads the stored field and nothing ever
  // recomputes it, so closing a TRY position while FX is unresolved under-reported by that
  // position's entire gain, forever. Refuse instead of fabricating. (audit 2026-08-01)
  const pnlUSD = toUSDStrict(pnl, cur);
  if (pnlUSD == null) return toast('No usable FX rate — the USD P&L for this exit cannot be computed and must not be stored as $0. Refresh prices or set a manual rate in Settings.', 'a-r', 9000);
  t.exits.push({
    date: now(),
    price: ep,
    qty: eq,
    reason: er,
    pnl,
    pnlUSD,
    currency: cur,
    leverage: t.leverage
  });
  t.qty -= eq;
  if (t.qty <= 0) {
    t.status = 'Closed';
    t.closeDate = now();
  } else t.status = 'Partial';
  stampItem(t); // CRITICAL: bump _updatedAt so Supabase merge keeps local mutation
  S.realized.push({
    id: uid(),
    tradeId: t.id,
    date: now(),
    ticker: t.ticker,
    qty: eq,
    entry: t.entry,
    exit: ep,
    pnl,
    pnlUSD,
    currency: cur,
    reason: er,
    leverage: t.leverage
  });
  const margin = t.entry * eq;
  const rawProceeds = margin + pnl;
  // T-1: settle the true amount. If a leveraged loss exceeds posted margin, debit the shortfall from cash
  // instead of clamping to 0 (which understated losses and broke the cash ↔ ledger invariant).
  const marginCall = rawProceeds < 0;
  const proceeds = rawProceeds;
  if (marginCall) {
    toast('Margin call: loss exceeds margin by ' + fc(Math.abs(rawProceeds)) + ' — debited from cash', 'a-r');
  }
  S.cash[cur] = (S.cash[cur] || 0) + proceeds;
  S.cashTxs.unshift({
    id: uid(),
    tradeId: t.id,
    date: now(),
    type: 'trade_exit',
    amount: proceeds,
    currency: cur,
    note:
      t.ticker +
      ' ' +
      er +
      ' (' +
      fc(proceeds) +
      ' proceeds, P&L:' +
      fc(pnl) +
      '/' +
      fU(pnlUSD) +
      (t.leverage > 1 ? ' @' + t.leverage + 'x' : '') +
      ')'
  });
  console.log(
    'trade',
    t.ticker,
    `${er}: ${f(eq)} @ ${fc(ep)} P&L:${fc(pnl)} [${fU(pnlUSD)}]`
  );
  save();
  markDirty('cash', 'dash');
  clMo('moTE');
  renderT();
  toast(t.ticker + ': ' + fc(pnl), pnl >= 0 ? 'a-g' : 'a-r');
  // Advisory followed-loop: if this exit was initiated from an Apply-TP rec, the acted
  // event lands NOW (on confirm), not at dialog-open (over-count fix 2026-07-22).
  if (typeof window.__advisoryExitHook === 'function') { try { window.__advisoryExitHook(t.id); } catch (_e) { /* non-critical */ } }
  // Offer AI review for fully closed trades
  if (t.status === 'Closed' && typeof window.offerPostExitReview === 'function') {
    window.offerPostExitReview(t);
  }
}

// ===== Add to Position (Scale-In) =====

let addPosTradeId = null;

function openAddPosition(id) {
  const t = S.trades.find(x => x.id === id);
  if (!t) return;
  if (t.status === 'Closed' || t.type === 'TimeDeposit') return toast('Cannot add to this trade', 'a-r');
  addPosTradeId = id;
  const cur = t.currency || 'USD';
  const fc = v => fByCur(cur, v);
  // Pre-fill with current price if available
  const cp = S.prices[t.ticker] ? S.prices[t.ticker].price : '';
  document.getElementById('taPrice').value = cp;
  document.getElementById('taQty').value = '';
  document.getElementById('taDate').value = new Date().toISOString().slice(0, 10);
  // Show current position info
  const info = document.getElementById('taInfo');
  if (info) {
    info.innerHTML = `<strong>${esc(t.ticker)}</strong> ${esc(t.side)} — Current: ${f(t.qty, t.type === 'Crypto' ? 6 : 2)} @ ${fc(t.entry)} (${t.leverage}x)`;
  }
  // Clear preview
  const prev = document.getElementById('taPreview');
  if (prev) prev.innerHTML = '';
  document.getElementById('moTA').classList.add('active');
  // Live preview on input
  const updatePreview = () => {
    const np = parseFloat(document.getElementById('taPrice').value);
    const nq = parseFloat(document.getElementById('taQty').value);
    if (!np || !nq || np <= 0 || nq <= 0) { if (prev) prev.innerHTML = ''; return; }
    const oldCost = t.entry * t.qty;
    const addCost = np * nq;
    const newQty = t.qty + nq;
    const newAvg = (oldCost + addCost) / newQty;
    if (prev) prev.innerHTML = `New avg: <strong>${fc(newAvg)}</strong> &times; ${f(newQty, t.type === 'Crypto' ? 6 : 2)} — Cost: ${fc(addCost)} from cash`;
  };
  document.getElementById('taPrice').oninput = updatePreview;
  document.getElementById('taQty').oninput = updatePreview;
}

function execAddPosition() {
  const t = S.trades.find(x => x.id === addPosTradeId);
  if (!t) return;
  const np = parseFloat(document.getElementById('taPrice').value);
  const nq = parseFloat(document.getElementById('taQty').value);
  const nd = document.getElementById('taDate').value;
  if (!np || np <= 0) return toast('Entry price must be positive', 'a-r');
  if (!nq || nq <= 0) return toast('Quantity must be positive', 'a-r');
  if (!nd) return toast('Date required', 'a-r');

  const cur = t.currency || 'USD';
  const fc = v => fByCur(cur, v);

  // Calculate weighted average entry
  const oldCost = t.entry * t.qty;
  const addCost = np * nq;
  const newQty = t.qty + nq;
  const newAvg = (oldCost + addCost) / newQty;

  // Initialize lots array if needed (first lot = original position)
  if (!t.lots) t.lots = [{ date: t.openDate || now(), price: t.entry, qty: t.qty }];
  // Add new lot
  t.lots.push({ date: nd + 'T12:00:00', price: np, qty: nq });

  // Update trade fields
  t.entry = newAvg;
  t.qty = newQty;
  t._originalQty = (t._originalQty || t.qty - nq) + nq;
  if (t.status === 'Partial') t.status = 'Open'; // re-open if was partial
  stampItem(t);

  // Deduct cost from cash
  S.cash[cur] = (S.cash[cur] || 0) - addCost;
  S.cashTxs.unshift({
    id: uid(),
    tradeId: t.id,
    date: nd + 'T12:00:00',
    type: 'trade_add',
    amount: -addCost,
    currency: cur,
    note: `Add ${t.ticker} ${f(nq, t.type === 'Crypto' ? 6 : 2)} @ ${fc(np)} → avg ${fc(newAvg)}`,
    _updatedAt: Date.now()
  });

  console.log('trade', t.ticker, `Scale-in: +${f(nq)} @ ${fc(np)} → avg ${fc(newAvg)} total ${f(newQty)}`);
  addPosTradeId = null;
  save();
  markDirty('cash', 'dash');
  clMo('moTA');
  renderT();
  toast(`${t.ticker}: added ${f(nq)} @ ${fc(np)}`, 'a-g');
}

// ===== Exports =====

export {
  editTrId,
  notify,
  scanTradeCrossings,
  scanMaturities,
  inlineTPrice,
  manTP,
  toggleTdTradeFields,
  onTdKindChange,
  calcTdTradePreview,
  calcExpPnl,
  renderT,
  renderTradeJournal,
  openAddT,
  editTr,
  saveT,
  openExit,
  execExit,
  openAddPosition,
  execAddPosition
};
